/**
 * The Document driver — ADR-006 §2.2: every driver authors the same Document,
 * and this is the one execution path they share. The app runs it with a
 * viewport attached; the agent and the corpus run it with none (P4 is this
 * function, headless — proven by construction, not scheduled). Sequencing
 * only: specimens resolve through the injected IO, imports go through the
 * orchestrator, hand work through the ADR-010 §11 spine, clips retarget by
 * NAME. Zero engine types (depcruise: session-no-engine).
 * Depends on: kernel (Document + hand spine + retarget), adapter-three
 * (loaders, hand isolation), import pipeline. Depended on by: app shell,
 * session tests, the corpus batch when it lands.
 */
import { retargetEvidenceMarkers } from './retarget-markers.js';
import {
  bindGraftedDigits,
  fbxCharacterToGlb,
  loadGLB,
  materializeMissingBones,
  applyChestRepair,
  rescueOrphanSkinVertices,
  soleSkinnedMesh,
  stripBonesFromSkin,
  allSkinnedMeshes,
  skinnedMeshesOfSelectedSkeleton,
  reownPelvisFlesh,
  seatSharedBone,
  seatSourceFor,
  type PelvisReownReport,
  findBoneByName,
  isolateHandRegion,
  recenterSceneBoneBind,
  recenterSceneBoneBindAcross,
  clearHingeCorrection,
  readSceneElbowPlacement,
  writeSceneElbowPlacement,
  readSceneShoulderRecenter,
  writeSceneShoulderRecenter,
  reseatSceneBoneBind,
  readSceneHeadSeatingMarker,
  writeSceneHeadSeatingMarker,
  readSceneArmSpace,
  bindWorldOwnership,
  severedForearmChain,
  reweightHingeBand,
  reweightMuscleBelly,
  measureLeafGirth,
  captureRestDetail,
  deriveCreaseDeltas,
  morphedBindPosition,
  derivationAngle,
  graftCreaseMorph,
  poseHingeAt,
  subjectArrays,
  writeSubjectBindPositions,
  type LeafGirthReading,
  type HingeReweightReport,
  type LoadedFbx,
  type LoadedGlb,
  type SlAnimDroppedJoint,
  type WristReown,
} from '@harryhausen/adapter-three';
import {
  analyzeSeparationProfile,
  authorHandFrames,
  authoredClipData,
  computeBindWeights,
  detectRigFamily,
  graftDigits,
  graftHingeHelpers,
  pelvisOwnershipFor,
  resolvePelvisOwnership,
  type ResolvedPelvisOwnership,
  hingeTemplateFor,
  isCorrectiveBone,
  hasPlayableKeys,
  notDesignedYet,
  parsePriorsBundle,
  parseDigitPin,
  stripDigitsFromRig,
  StripDigitsRefusal,
  pinsFromBranches,
  placeDigits,
  proposeDigitNames,
  distanceToOccupied,
  worldPointToVoxel,
  recenterBoneBind,
  reseatBoneBind,
  armSpacePlan,
  ArmSpaceRefusal,
  headSpaceReseat,
  headSpaceReseatDelta,
  headSeatingDeltaDegrees,
  HeadSpaceRefusal,
  HEAD_SPACE_MAX_DEGREES,
  HEAD_SPACE_SEAT,
  type HeadSeatingMarker,
  reownedVertices,
  wristStationMove,
  wristSeatUnderPin,
  STATION_SEAT_LINK_FRACTION,
  STATION_SEAT_SLAB_HALF_FRACTION,
  limbAxisCentre,
  resolveLimb,
  shoulderCapSeat,
  ShoulderCapRefusal,
  quatRotateVec3,
  quatInvertUnit,
  vec3Add,
  vec3Sub,
  retargetClip,
  voxelizeMesh,
  voxelPointToWorld,
  robustCutCentre,
  fingerprintMesh,
  fingerprintMismatch,
  turnGlbAboutY,
  type MeshFingerprint,
  placeTail,
  graftTail,
  tailBindInfluences,
  tailPivotSeam,
  recentreTail,
  meshHeight,
  type LegCapsule,
  tailParentBone,
  TailRefusal,
  type AnimatorDocument,
  type BindVertex,
  type BoneSegment,
  type VertexInfluence,
  type VolumeGrid,
  type VoxelPoint,
  type ClipData,
  type ClipSelection,
  type Confirmation,
  type DigitName,
  type EvidenceMarker,
  type HandSession,
  type JointFrame,
  type ResolvedHinge,
  type RetargetResult,
  type RigDescriptor,
  type BranchChain,
  type DigitPin,
  type SeparationProfile,
  type SpecimenRef,
  type TargetSession,
} from '@harryhausen/kernel';
import { bindPoseVertices } from '../hands/elbow-evidence.js';
import { dominantFleshOf, upperArmSegmentBones } from '../hands/shoulder-evidence.js';
import { meshyHeadBaseline } from './meshy-head-baseline.js';
import { planSoftTissue, SoftTissueRunRefusal, type SoftTissueGraftRecord, type SoftTissuePlan } from '../jiggle/soft-tissue-run.js';
import { graftSurfaceJiggle, surfaceJiggleLeaves, type SurfaceJiggleRecord } from '../jiggle/surface-jiggle.js';
import { adoptedHairPatch, finishHairRecord, planHairSoftening, type HairSoftenRecord } from '../hair/hair-soften-run.js';
import { CLUSTER_RADIUS_FRACTION } from '../hands/tip-consensus.js';
import {
  importBvh,
  importFbx,
  importGlb,
  importSlAnim,
  type FbxImportResult,
  type ImportDeps,
  type ImportResult,
  type SlAnimImportResult,
} from '../import/import-pipeline.js';

/**
 * ADR-030: a measured head-seating baseline smaller than this is applied as
 * zero. Float32 GLB binds resolve a rest pitch to roughly 1e-5°; 0.05° is
 * far below anything a person can see and far above the quantization floor,
 * so a character re-imported from its own seated export measures ~0 and is
 * NOT reseated a second time by its own residual. Explicit values ignore it.
 */
export const HEAD_SEATING_RESOLUTION_DEGREES = 0.05;

/**
 * What the ONE rule decides: the surgery on the bind AS LOADED, the total the
 * bind wears afterwards from the authored rest, who decided, and what the
 * file should declare so the next reopen agrees (ADR-030 §14).
 */
export interface HeadSeatingDecision {
  /** Degrees to reseat the loaded bind by (erect-ward positive); 0 = no surgery. */
  readonly degrees: number;
  /** The total from the authored rest afterwards: the file's declaration + `degrees`. */
  readonly total: number;
  readonly provenance: 'explicit' | 'baseline' | 'file' | 'unmeasured';
  /** The declaration the exported file carries; null when nothing was ever decided. */
  readonly marker: HeadSeatingMarker | null;
  /** The unclamped total when the baseline path would have left the band; null otherwise. */
  readonly clampedFrom: number | null;
}

/**
 * The ONE rule for what the armature wears — two doors, one value: the run
 * (below) and the app's in-place commit (main.ts). Values are TOTALS from
 * the authored rest; the surgery is the difference from what the file
 * already declares (`file`, the marker read off the loaded scene).
 *
 *   - an explicit value (zero included) is the total, and the file will
 *     declare it as a person's;
 *   - a file that declares a PERSON'S seating is honoured as it stands: the
 *     heuristic stands down (this is the reopen loop a declaration needs —
 *     the baseline re-measured against a seated bind is the gap the seating
 *     just closed, and applying it would undo the declaration; measured on
 *     Allen's fox onesie, 2026-08-27: +53.5° saved, −29.9° "re-measured");
 *   - otherwise the measured baseline moves the bind, unless it is below the
 *     resolution (a reopened export's own residual), and the file declares
 *     the resulting total as a measurement; the TOTAL is band-clamped,
 *     loudly, never the residual alone;
 *   - nothing measured and nothing declared: nothing moves, nothing written.
 */
export function decideHeadSeating(input: {
  readonly baseline: number | null;
  readonly explicit: number | undefined;
  readonly file: HeadSeatingMarker | null;
  readonly source: string | null;
}): HeadSeatingDecision {
  const { baseline, explicit, file, source } = input;
  const worn = file?.degrees ?? 0;
  if (explicit !== undefined) {
    return {
      degrees: explicit - worn,
      total: explicit,
      provenance: 'explicit',
      marker: { degrees: explicit, provenance: 'explicit', source: source ?? file?.source ?? null },
      clampedFrom: null,
    };
  }
  if (file?.provenance === 'explicit') {
    return { degrees: 0, total: worn, provenance: 'file', marker: file, clampedFrom: null };
  }
  if (baseline === null) {
    return { degrees: 0, total: worn, provenance: 'unmeasured', marker: file, clampedFrom: null };
  }
  if (Math.abs(baseline) < HEAD_SEATING_RESOLUTION_DEGREES) {
    return {
      degrees: 0,
      total: worn,
      provenance: 'baseline',
      marker: { degrees: worn, provenance: 'baseline', source },
      clampedFrom: null,
    };
  }
  const unclamped = worn + baseline;
  const total =
    Math.abs(unclamped) > HEAD_SPACE_MAX_DEGREES ? Math.sign(unclamped) * HEAD_SPACE_MAX_DEGREES : unclamped;
  return {
    degrees: total - worn,
    total,
    provenance: 'baseline',
    marker: { degrees: total, provenance: 'baseline', source },
    clampedFrom: total === unclamped ? null : unclamped,
  };
}

/** What the in-place commit did: the rig afterwards and what the app's projector rests need. */
export interface InPlaceHeadSeating {
  readonly seatedRig: RigDescriptor;
  readonly bone: string;
  /**
   * The surgery — or null when only the DECLARATION changed: a person
   * confirming the measured value (slider away and back to the exact
   * baseline) moves no bind, but the file must now say a person decided, or
   * a reopen against another source re-runs the heuristic on a neck the
   * Document calls hand-set (Codex P2, #126).
   */
  readonly reseat: {
    /** Left-multiply onto any STORED rest local of `bone` (pose projector seeds) — the scene reseat's report. */
    readonly localPreRotation: { readonly x: number; readonly y: number; readonly z: number; readonly w: number };
    readonly descendants: number;
    readonly skeletons: number;
  } | null;
  /** True when the marker on the scene was (re)written. */
  readonly declared: boolean;
}

const sameMarker = (a: HeadSeatingMarker | null, b: HeadSeatingMarker | null): boolean =>
  a === b ||
  (a !== null && b !== null && a.degrees === b.degrees && a.provenance === b.provenance && a.source === b.source);

/**
 * The app's IN-PLACE seating commit (ADR-030 §11 D1a) — the same two
 * surgeries the run applies, on a scene that already wears `worn` degrees
 * beyond the file's declaration, plus the marker kept current. Extracted so
 * the reopen test calls the code the browser runs (slider → export → drop),
 * not a re-enactment of it. Null when neither the bind nor the declaration
 * changes. The delta door is ungated: two in-band totals may differ by more
 * than the band.
 */
export function commitHeadSeatingInPlace(
  scene: DocumentRunResult['scene'],
  seatedRig: RigDescriptor,
  decision: HeadSeatingDecision,
  worn: number,
  selection: { readonly label: string; readonly selectSkeleton?: string },
): InPlaceHeadSeating | null {
  // The seat bone is unique on a rig that carries the property (the run's
  // capability probe passed before any commit can reach here).
  const bone = seatedRig.mapping.find((m) => m.ontologyId === HEAD_SPACE_SEAT)!.rigBone;
  const move = decision.degrees !== worn;
  const declare = !sameMarker(readSceneHeadSeatingMarker(scene, bone, selection), decision.marker);
  if (!move && !declare) return null;
  let next = seatedRig;
  let reseat: InPlaceHeadSeating['reseat'] = null;
  if (move) {
    const delta = headSpaceReseatDelta(seatedRig, decision.degrees - worn)!; // non-null: the difference is non-zero
    const report = reseatSceneBoneBind(scene, delta.bone, delta, selection);
    next = reseatBoneBind(seatedRig, delta.bone, delta);
    reseat = {
      localPreRotation: report.localPreRotation,
      descendants: report.descendants.length,
      skeletons: report.skeletons,
    };
  }
  // The declaration follows the commit: the next export says what the
  // armature wears and who decided (or that undo restored the measurement).
  if (declare) writeSceneHeadSeatingMarker(scene, bone, decision.marker, selection);
  return { seatedRig: next, bone, reseat, declared: declare };
}

/** Resolving a SpecimenRef to bytes is the environment's job, injected. */
export interface DocumentIO {
  loadSpecimen(ref: SpecimenRef): Promise<ArrayBuffer>;
}

/**
 * A Document refusal — the §8 contract speaking. Every message names the
 * value that refused and what would release it; a refusal list is a work
 * queue, not a crash.
 */
export class DocumentRefusal extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'DocumentRefusal';
  }
}

/** What one hand session did — the evidence surface renders from this. */
export interface HandGraftRecord {
  readonly handBone: string;
  readonly measuredProfile: string;
  /** Graded judgement, not a score — and confirming never raises it (brief §5.6). */
  readonly confidence: string;
  readonly digits: readonly string[];
  readonly graftedBoneCount: number;
  readonly jointFrameCount: number;
}

/**
 * What one hand BIND did (ADR-010 §2 ④) — the numbers that say the graft reaches the
 * mesh and not just the scene graph. `influencesEvicted`/`largestEviction` are the
 * honest cost of the four-influence ceiling: every digit weight displaced an existing
 * one, and this is how much was displaced. `worstWeightSumError` is partition of
 * unity measured rather than assumed — a renormalisation bug shows up as geometry
 * shrinking toward the origin, not as an exception.
 */
export interface HandBindRecord {
  readonly handBone: string;
  readonly bonesAdded: number;
  readonly verticesReweighted: number;
  readonly influencesEvicted: number;
  readonly largestEviction: number;
  readonly worstWeightSumError: number;
}

/**
 * What one TAIL did (ADR-031 §3.3a) — the placement and the reown, in numbers
 * a person (and the probes) can read: where the chain starts and ends, how
 * long each bone is, and how the zone's flesh was split between the chain and
 * the body. Bind-world metres throughout.
 */
export interface TailGraftRecord {
  readonly zoneId: string;
  readonly zone: string;
  /** The grafted rig bones, root → tip (`Tail1_graft`…). */
  readonly bones: readonly string[];
  readonly count: { readonly value: number; readonly provenance: 'declared' | 'default' };
  /** What the default rule would choose on this zone — shown beside a declared count. */
  readonly defaultCount: number;
  /** count + 1 joints, root → tip. */
  readonly joints: readonly { readonly x: number; readonly y: number; readonly z: number }[];
  readonly segmentLengths: readonly number[];
  /** Walked length root → tip. */
  readonly length: number;
  /** Mean cross-section radius along the walk. */
  readonly meanRadius: number;
  /**
   * The cross-section radius at each joint, world units, root → tip (count + 1)
   * — the taper evidence, and what a spring chain's hit radius is read from
   * (ADR-038's tail defaults).
   */
  readonly radii: readonly number[];
  /** The cut: root and the normal pointing away from the body. */
  readonly cut: { readonly point: readonly [number, number, number]; readonly normal: readonly [number, number, number] };
  /** The root as measured (the junction ring's centre); `joints[0]` is the person's pivot when one is declared. */
  readonly measuredRoot: { readonly x: number; readonly y: number; readonly z: number };
  /** The bind seam's width from a placed pivot (world units; 0 = none declared, a one-voxel seam). */
  readonly pivotSeam: number;
  readonly voxelSize: number;
  readonly verticesOwned: number;
  readonly verticesBlended: number;
  readonly verticesRetained: number;
  /** Per tail bone, how many vertices it dominates — the chain-coverage evidence. */
  readonly dominance: readonly { readonly bone: string; readonly vertices: number }[];
}

export interface DocumentClipRun {
  readonly selection: ClipSelection;
  readonly sourceLabel: string;
  readonly result: RetargetResult;
}

/**
 * ADR-030 — what the run did about head seating. Recorded zero included: a
 * deliberate 0°, a measured 0°, and a rig that cannot carry the property are
 * three different facts, and the panel renders them as three different
 * states.
 */
export interface HeadSeatingRecord {
  /** Degrees the LOADED bind was reseated by (erect-ward positive); 0 = no surgery. */
  readonly degrees: number;
  /**
   * The total the armature wears from its AUTHORED rest: what the file
   * declared on arrival plus `degrees`. The number the panel shows and the
   * number a Document's `edits.headSpace` means (ADR-030 §14).
   */
  readonly total: number;
  /**
   * 'explicit' = `edits.headSpace` (zero included); 'baseline' = the measured
   * per-pair delta (edit absent); 'file' = edit absent and the file declares a
   * person's seating, honoured as it stands; 'unmeasured' = edit absent and
   * no source pair could be measured (no clips, or an unmeasurable source).
   */
  readonly provenance: 'explicit' | 'baseline' | 'file' | 'unmeasured';
  /**
   * The measured baseline on the bind AS LOADED (unclamped; the decision
   * clamps the total), or null when no source pair could be measured.
   */
  readonly baseline: number | null;
  /** What the file declared on arrival (its `extras` marker), or null. */
  readonly file: HeadSeatingMarker | null;
  /** The source label the baseline was measured against, or null. */
  readonly source: string | null;
  /** The bone whose bind carried the surgery (its subtree followed), or null when the rig refused. */
  readonly bone: string | null;
  /** Why the rig cannot carry head-space, or null. */
  readonly refused: string | null;
}

export interface DocumentRunResult {
  /** Pre-graft import evidence: mapping, reports, facing promotion. */
  readonly targetImport: ImportResult;
  /**
   * The final rig — grafts applied, kernel-gated — in the RETARGET FRAME: the
   * bind every clip in `clips` was made against, and the skeleton
   * exportAnimationOnly writes, so the clip library is bit-identical whatever
   * the seating (ADR-016, ADR-030 §12).
   */
  readonly rig: RigDescriptor;
  /**
   * The armature AS SHIPPED (ADR-030): `rig` with the head seating applied —
   * the bind the scene wears and exportGlb writes. The same object as `rig`
   * when nothing was reseated. A door that reads a Neck/Head rest local must
   * read this one (the preview filter's untracked-bone synthesis).
   */
  readonly seatedRig: RigDescriptor;
  /** ADR-030 — what the run did about head seating, zero included. */
  readonly headSeating: HeadSeatingRecord;
  /**
   * The arm-space the FILE declares on its upper-arm nodes (ADR-030 §13,
   * ruled: a play-time property carried as a declaration), or null when it
   * declares none or the rig cannot carry arm-space. The app's filter reads
   * `edits.armSpace ?? armSpaceFile ?? 0`, so a reopened export wears what
   * was set; the export door writes the effective value back.
   */
  readonly armSpaceFile: number | null;
  /** The renderable scene (a VIEW — rebuildable, never the truth). */
  readonly scene: LoadedGlb['scene'] | LoadedFbx['scene'];
  readonly materializedBones: readonly string[];
  readonly handGrafts: readonly HandGraftRecord[];
  /** One per bound hand (and one `tail:<zone>` per bound tail) — empty when nothing was grafted. */
  readonly handBinds: readonly HandBindRecord[];
  /** ADR-031 T2 — one per declared tail, empty when none. */
  readonly tailGrafts: readonly TailGraftRecord[];
  /** ADR-038 J2 — one per declared soft-tissue leaf (and the hair's, ADR-039 T2a), empty when none. */
  readonly softTissueGrafts: readonly SoftTissueGraftRecord[];
  /**
   * ADR-039 T2a — what the hair pass did with a CONFIRMED hair (softened, adopted from
   * the file, deferred to T3, refused — each with its sentence), or null when no hair is
   * confirmed (an unconfirmed offer changes nothing).
   */
  readonly hair: HairSoftenRecord | null;
  /** ADR-038 §14 J4 — one per grown leaf when the surface jiggle ran (`RunOptions.surfaceJiggle`), else empty. */
  readonly surfaceJiggle: readonly SurfaceJiggleRecord[];
  /**
   * The mesh a Zone's faces are valid against (ADR-031 D2), when it is NOT
   * the live one: CENTRE THE TAIL turns the rest flesh, so the live bind
   * positions no longer hash to the mesh the zone was cut on. Taken before
   * the turn. null = nothing moved the flesh; the live mesh is that mesh.
   * (Field read 2026-09-25, the dragon: a centred tail read its own zone as
   * "cut on another mesh", the detector offered the tail again, and a second
   * pin declared two tails.)
   */
  readonly zoneFingerprint: MeshFingerprint | null;
  /** The declared FACE FORWARD turn this run applied, or null (none declared). */
  readonly faceForward: { readonly turned: boolean; readonly reason: string } | null;
  /**
   * ADR-028 resolved hinges — helper bone, driver, and the evaluator profile
   * with axes MEASURED from this rig's bind. The live driver and the bake
   * pass both consume exactly this; empty when the family declares none.
   */
  readonly hinges: readonly ResolvedHinge[];
  /** One per hinge — the crease band's honest weight accounting. */
  readonly hingeBinds: readonly HingeReweightReport[];
  /**
   * ADR-029 — the pelvis re-own's honest weight accounting, one per skinned
   * mesh; empty when the family declares none or the rig could not resolve
   * a station (see bindMarkers).
   */
  readonly pelvisReowns: readonly PelvisReownReport[];
  /**
   * Named evidence from weight computation — a bone whose segment fell outside the
   * volume, vertices the voxel field could not reach. Real outcomes of real
   * specimens, surfaced rather than swallowed: an unseeded bone drives nothing, and
   * a session must be able to see that it drives nothing (ADR-010 §7).
   */
  readonly confirmedElbows: readonly string[];
  readonly bindMarkers: readonly EvidenceMarker[];
  readonly jointFrames: readonly JointFrame[];
  /** Bones the pose layer drove (ADR-007 §5) — evidence, never a silent write. */
  readonly posedBones: readonly string[];
  /**
   * The imported REST rotation of each posed bone, captured BEFORE the pose
   * overwrote it. A live projector (app/src/session/pose-projection) seeds its
   * rest map from this so undoing a pose loaded WITH the Document restores the
   * imported rest, not the loaded pose (Codex P2, #54). Empty when no pose was
   * pre-applied.
   */
  readonly poseRests: readonly { readonly bone: string; readonly rotation: { x: number; y: number; z: number; w: number } }[];
  readonly sources: ReadonlyMap<string, ImportResult | FbxImportResult | SlAnimImportResult>;
  /**
   * SL sources whose files name joints outside the canonical skeleton — the
   * curves this run could NOT carry (ADR-191). The import door reports these
   * on SlAnimImportResult; the Document rail must not erase them (Codex P2,
   * #87): a corpus caller publishing incomplete motion has to be able to see
   * the loss without re-importing. Empty when every joint landed.
   */
  readonly slDroppedJoints: readonly { readonly source: string; readonly joints: readonly SlAnimDroppedJoint[] }[];
  readonly clips: readonly DocumentClipRun[];
  /**
   * Authored clips converted to playable ClipData (ADR-006 §13.4) — the
   * projection of `Document.clips` the caller's mixer samples.
   */
  readonly authoredClips: readonly { readonly name: string; readonly clip: ClipData }[];
  /** Authored clips with no keys — reported, never silently skipped (§8). */
  readonly emptyAuthoredClips: readonly string[];
}

async function loadVerified(io: DocumentIO, ref: SpecimenRef, label: string): Promise<ArrayBuffer> {
  const bytes = await io.loadSpecimen(ref);
  if (ref.sha256 !== undefined) {
    const digest = await crypto.subtle.digest('SHA-256', bytes);
    const hex = [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('');
    if (hex !== ref.sha256) {
      throw new DocumentRefusal(
        `specimen "${label}" (${ref.uri}): bytes do not match the Document's sha256 pin ` +
          `(expected ${ref.sha256}, loaded ${hex}) — the file changed since this work was authored.`,
      );
    }
  }
  return bytes;
}

/**
 * A confirmation is a human act recorded in the Document; an unattended run
 * over unconfirmed reads refuses and reports (§8). The refusal names the
 * affordance and the release, because the refusal list IS the attention queue.
 */
function requiredConfirmation(target: TargetSession, hand: HandSession, kind: Confirmation['target']): Confirmation {
  const found = target.confirmations.find((c) => c.target === kind && c.scope === hand.handBone);
  if (!found) {
    throw new DocumentRefusal(
      `hand "${hand.handBone}": no ${kind} confirmation in the Document — the engine cannot sign its ` +
        `own homework (§8). A human confirms the value for this hand, or this affordance stays refused.`,
    );
  }
  if (found.evidenceDigest !== undefined) {
    // §8 evidence-binding: honoring a digest we cannot yet verify would be a
    // signature on an unread page. Loud until the digest pipeline is designed.
    notDesignedYet(`Document confirmation evidenceDigest (${kind} @ ${hand.handBone})`);
  }
  return found;
}

function optionalConfirmation(
  target: TargetSession,
  hand: HandSession,
  kind: Confirmation['target'],
): Confirmation | undefined {
  const found = target.confirmations.find((c) => c.target === kind && c.scope === hand.handBone);
  if (found?.evidenceDigest !== undefined) {
    notDesignedYet(`Document confirmation evidenceDigest (${kind} @ ${hand.handBone})`);
  }
  return found;
}

/**
 * This hand's pinned digit tips, in BIND-WORLD, or `null` when none were given.
 * One confirmation per digit — see the Document's `digitAssignment` docs. The
 * schema has already rejected a malformed value, a digit pinned twice, two
 * digits on one place, and the retired branch-keyed form, so this only parses
 * what it is handed.
 */
function digitPinsFor(
  target: TargetSession,
  hand: HandSession,
): { digit: DigitName; tip: { x: number; y: number; z: number }; knuckle?: { x: number; y: number; z: number } }[] | null {
  const entries = target.confirmations.filter((c) => c.target === 'digitAssignment' && c.scope === hand.handBone);
  if (entries.length === 0) return null;
  // The optional second row (ADR-024 §2.2): the schema has already refused an
  // orphan knuckle and a digit knuckled twice, so lookup is all that is left.
  const knuckles = new Map<string, { x: number; y: number; z: number }>();
  for (const c of target.confirmations) {
    if (c.target !== 'digitKnuckle' || c.scope !== hand.handBone) continue;
    if (c.evidenceDigest !== undefined) {
      notDesignedYet(`Document confirmation evidenceDigest (digitKnuckle @ ${hand.handBone})`);
    }
    const pin = parseDigitPin(c.value)!; // schema-guaranteed
    knuckles.set(pin.digit, pin.tip);
  }
  return entries.map((c) => {
    if (c.evidenceDigest !== undefined) {
      notDesignedYet(`Document confirmation evidenceDigest (digitAssignment @ ${hand.handBone})`);
    }
    const pin = parseDigitPin(c.value)!; // schema-guaranteed
    const knuckle = knuckles.get(pin.digit);
    return { digit: pin.digit as DigitName, tip: pin.tip, ...(knuckle === undefined ? {} : { knuckle }) };
  });
}

/**
 * How far off the hand volume a pin may sit before it stops being a pin
 * (ADR-022 §9). Two voxels: enough to absorb the voxel-centre convention, the
 * fractional conversion, and a surface point on a cropped grid — far short of
 * the "this coordinate belongs to another model" distances the refusal is for.
 */
const PIN_REGION_MARGIN_VOXELS = 2;

/**
 * The knuckle's own margin, and it is wider for a measured reason (Eric_new_v3,
 * field 2026-08-14): a knuckle is a mid-digit dorsal click, and on a very
 * low-poly hand its nearest triangles are the region's BOUNDARY skirt — kept
 * out by the isolation's 'all-in' policy (their far corners are cuff blends) —
 * so the voxel shell has a small hole exactly where the click lands. Eric's
 * correctly-aimed thumb knuckle measured 2.1–2.6 voxels and was refused at
 * margin 2, twice. This refusal exists to catch a COORDINATE THAT BELONGS TO
 * ANOTHER MODEL — a distance measured in hundreds of voxels — not a shell gap;
 * four voxels stays far short of that while clearing the measured holes. The
 * placement itself is unbothered either way: `medialPath` snaps both ends into
 * the volume before walking.
 */
const KNUCKLE_REGION_MARGIN_VOXELS = 4;

/**
 * THE WITNESS (ADR-022 §3.3). Attach each pinned tip to the nearest branch tip
 * this resolution actually separated, and say — per digit — whether the volume
 * corroborates the person or has nothing to show there.
 *
 * This is the whole posture change made concrete. The branch is no longer
 * permission to place a digit; it is evidence ABOUT one, and it carries exactly
 * two things: the thumb's chain (the one placement input a branch still
 * supplies, §2), and a distance a human can judge. A pin with no branch within
 * the radius places anyway and says `pin-unsupported` — that is a fused digit,
 * a mitten, or a sixth finger, and refusing it was the dead end this ADR ends.
 *
 * Radius is the consensus clustering radius (a fraction of the hand's span), so
 * "near enough to be the same digit" means one thing across the codebase.
 */
function witnessPins(
  pins: readonly { digit: DigitName; tip: { x: number; y: number; z: number }; knuckle?: { x: number; y: number; z: number } }[],
  branches: readonly BranchChain[],
  grid: VolumeGrid,
  wristCentre: VoxelPoint,
  handBone: string,
  markers: EvidenceMarker[],
): DigitPin[] {
  const inVoxels = pins.map((p) => ({
    ...p,
    voxelTip: worldPointToVoxel(grid, p.tip),
    voxelKnuckle: p.knuckle === undefined ? undefined : worldPointToVoxel(grid, p.knuckle),
  }));

  // A PIN OFF THE HAND IS A REFUSAL, NOT A JUDGEMENT (ADR-022 §9; Codex P2, #92
  // caught that T2 stated this rule and did not implement it). `pin-unsupported`
  // means "the volume separated nothing here" — a fused digit, a mitten, Bart's
  // sixth finger — and those are answers. A pin that is not on the hand AT ALL
  // is a different thing: a stale Document whose specimen changed under it, or a
  // hand-edited coordinate. Placement would extrapolate wrist→tip straight through
  // it and graft an enormous detached finger, so it stops here, loudly, naming
  // the distance. The margin is generous on purpose — a real fingertip is a
  // SURFACE point, and the surface is exactly where voxel quantization is worst.
  // A KNUCKLE gets the same protection for the same reason: it becomes the
  // digit's BASE (ADR-024 §2.2), and a base in empty space aims the whole chord.
  for (const { digit, voxelTip, voxelKnuckle } of inVoxels) {
    for (const [what, at, margin] of [
      ['pin', voxelTip, PIN_REGION_MARGIN_VOXELS],
      ['knuckle', voxelKnuckle, KNUCKLE_REGION_MARGIN_VOXELS],
    ] as const) {
      if (at === undefined) continue;
      const away = distanceToOccupied(grid, at);
      if (away > margin) {
        throw new DocumentRefusal(
          `hand "${handBone}": the ${digit} ${what} is ${away.toFixed(1)} voxels from the hand volume ` +
            `(margin ${margin}) — it does not sit on this hand. The specimen has probably ` +
            `changed since it was placed; re-pin the digit rather than grafting a finger into empty space.`,
        );
      }
    }
  }
  const span = inVoxels.reduce(
    (m, p) => Math.max(m, Math.hypot(p.voxelTip.x - wristCentre.x, p.voxelTip.y - wristCentre.y, p.voxelTip.z - wristCentre.z)),
    0,
  );
  const radius = CLUSTER_RADIUS_FRACTION * span;
  const claimed = new Set<string>();
  return inVoxels.map(({ digit, voxelTip, voxelKnuckle }) => {
    const knuckle = voxelKnuckle === undefined ? {} : { knuckle: voxelKnuckle };
    let best: BranchChain | null = null;
    let bestD = radius;
    for (const b of branches) {
      if (claimed.has(b.id)) continue;
      const tip = b.chain[b.chain.length - 1]!;
      const d = Math.hypot(tip.x - voxelTip.x, tip.y - voxelTip.y, tip.z - voxelTip.z);
      if (d < bestD) {
        bestD = d;
        best = b;
      }
    }
    if (best === null) {
      markers.push({
        code: 'pin-unsupported',
        detail: {
          hand: handBone,
          digit,
          searchRadiusVoxels: Number(radius.toFixed(3)),
          branchesConsidered: branches.length,
        },
      });
      return { digit, tip: voxelTip, ...knuckle, sourceId: `pin:${digit}` };
    }
    claimed.add(best.id);
    markers.push({
      code: 'pin-corroborated',
      detail: { hand: handBone, digit, branch: best.id, distanceVoxels: Number(bestD.toFixed(3)) },
    });
    return { digit, tip: voxelTip, ...knuckle, chain: best.chain, sourceId: `pin:${digit}` };
  });
}

/**
 * Run a Document: import the target, graft its declared hands, import the
 * sources, retarget every clip selection. Headless by construction — a
 * viewport is something the CALLER points at the result (§3.3: the scene is
 * a view; this function computes truth).
 */
/**
 * What a tail's default count reads from the body and the zone: the body's
 * height (the shortest-segment floor) and whether the detector measured it a
 * flattened tube — a PLATE (its source detail says "flat").
 */
export function tailShapeOptions(zone: { readonly sources: readonly { readonly detail: string }[] }, positions: ArrayLike<number>): { bodyHeight: number; plate?: true } {
  const plate = zone.sources.some((src) => /^tail@\S+ flat /.test(src.detail));
  return { bodyHeight: meshHeight(positions), ...(plate ? { plate: true as const } : {}) };
}

/**
 * The thighs as capsules for the tail re-centre's collision stop: hip joint
 * (UpLeg) → knee (Leg), radius the median distance of the flesh the thigh
 * owns (dominant weight) from that segment. A rig without the mapping gives
 * none — the turn is then unguarded, and the marker says what stopped it.
 */
function legCapsules(rig: RigDescriptor, arrays: ReturnType<typeof subjectArrays>): LegCapsule[] {
  const byId = (id: string) => rig.bones.find((b) => b.name === rig.mapping.find((m) => m.ontologyId === id)?.rigBone);
  const out: LegCapsule[] = [];
  for (const [up, knee] of [
    ['LeftUpLeg', 'LeftLeg'],
    ['RightUpLeg', 'RightLeg'],
  ] as const) {
    const a = byId(up);
    const b = byId(knee);
    if (a === undefined || b === undefined) continue;
    const pa = a.worldBind.position;
    const pb = b.worldBind.position;
    const at = arrays.bones.findIndex((x) => x.name === a.name);
    const d: number[] = [];
    const n = arrays.positions.length / 3;
    for (let v = 0; at >= 0 && arrays.skin && v < n; v++) {
      let bw = 0;
      let bb = -1;
      for (let k = 0; k < 4; k++) {
        const w = arrays.skin.weight[v * 4 + k]!;
        if (w > bw) {
          bw = w;
          bb = arrays.skin.index[v * 4 + k]!;
        }
      }
      if (bb !== at) continue;
      const px = arrays.positions[v * 3]!;
      const py = arrays.positions[v * 3 + 1]!;
      const pz = arrays.positions[v * 3 + 2]!;
      const abx = pb.x - pa.x;
      const aby = pb.y - pa.y;
      const abz = pb.z - pa.z;
      const l2 = abx * abx + aby * aby + abz * abz;
      const t = l2 > 0 ? Math.min(1, Math.max(0, ((px - pa.x) * abx + (py - pa.y) * aby + (pz - pa.z) * abz) / l2)) : 0;
      d.push(Math.hypot(px - pa.x - t * abx, py - pa.y - t * aby, pz - pa.z - t * abz));
    }
    if (d.length === 0) continue;
    d.sort((x, y) => x - y);
    out.push({ name: a.name, a: pa, b: pb, radius: d[Math.floor(d.length / 2)]! });
  }
  return out;
}

/** Run-time switches the Document does not carry (prototypes behind a flag, ADR policy: explicit, never global). */
export interface RunOptions {
  /** ADR-038 §14 (J4) prototype: graft the surface jiggle's modes onto grown soft masses. Off by default. */
  readonly surfaceJiggle?: boolean;
}

export async function runDocument(
  doc: AnimatorDocument,
  io: DocumentIO,
  deps: ImportDeps,
  options: RunOptions = {},
): Promise<DocumentRunResult> {
  // ---- Target: bytes → orchestrated import (+ a renderable scene; the
  //      deliberate double-parse keeps the orchestrator engine-free).
  //      Mesh-less formats (bvh/anim) are SOURCE-only: a target must carry a
  //      mesh to skin and export — refuse by name, not downstream (§8).
  if (doc.target.specimen.format === 'bvh' || doc.target.specimen.format === 'anim') {
    throw new DocumentRefusal(
      `target "${doc.target.label}" declares format '${doc.target.specimen.format}' — a mesh-less ` +
        `animation source (ADR-003 §12) cannot be a target. Targets need a skinned mesh: glb or fbx.`,
    );
  }
  const fileBytes = await loadVerified(io, doc.target.specimen, doc.target.label);
  // FACE FORWARD (Allen, 2026-09-25: the backward bear, the bear girl): the
  // DECLARED turn (the load door measured it once on the new body) is applied to
  // the BYTES before the import and the scene both parse them, so the rig, the
  // hands, the tails and the export all see one forward-facing body. Replayed,
  // never re-measured: a Document without it loads exactly as authored.
  // An FBX CHARACTER is made a GLB first (fbx-character.ts: one armature,
  // metres, PBR, its textures waited for) — converted once per buffer and
  // replayed on every run, so from here on the body walks the GLB door.
  const fbxBody = doc.target.specimen.format === 'fbx' ? await fbxCharacterToGlb(fileBytes, doc.target.label) : null;
  const glbBytes = fbxBody?.glb ?? fileBytes;
  const declaredTurn = doc.target.facing;
  const faceForward = declaredTurn === undefined ? null : { turned: true, reason: declaredTurn.reason };
  const targetBytes = declaredTurn === undefined ? glbBytes : turnGlbAboutY(glbBytes, (declaredTurn.turnDeg * Math.PI) / 180);
  const importOptions = {
    label: doc.target.label,
    profile: doc.target.profile,
    ...(doc.target.selectSkeleton !== undefined ? { selectSkeleton: doc.target.selectSkeleton } : {}),
  };
  const [targetImport, loaded] = await Promise.all([importGlb(targetBytes, deps, importOptions), loadGLB(targetBytes)]);

  // ---- Hands (ADR-010 §11): per-hand params from the Session, thumb pin and
  //      profile declaration from CONFIRMATIONS — never from the analyzer.
  let rig = targetImport.rig;
  const jointFrames: JointFrame[] = [];
  const handGrafts: HandGraftRecord[] = [];
  const pendingBinds: {
    handBone: string;
    boneNames: string[];
    influences: readonly VertexInfluence[];
    reown?: WristReown;
  }[] = [];
  const bindMarkers: EvidenceMarker[] = [];
  if (faceForward?.turned === true) bindMarkers.push({ code: 'target-faced-forward', detail: { reason: faceForward.reason } });
  // Skin-weight hygiene at the load door (v2's neutral_bone rescue, widened to
  // zero/NaN-sum verts — the purple monster's lone uninfluenced vert). Runs
  // BEFORE isolation/binding so everything downstream, export included,
  // inherits the repair. Automatic, never silent: each repaired mesh is a
  // marker the evidence surface can show.
  for (const rescue of rescueOrphanSkinVertices(loaded.scene)) {
    bindMarkers.push({
      code: 'orphan-vertices-rescued',
      detail: {
        mesh: rescue.mesh,
        donated: rescue.donated,
        donatedTo: Object.entries(rescue.donatedTo)
          .map(([bone, n]) => `${bone}×${n}`)
          .join(', '),
        reseated: rescue.reseated,
        renormalized: rescue.renormalized,
        deadBones: rescue.deadBones.join(', ') || '(none — zero-weight only)',
        reseatedTo: Object.entries(rescue.reseatedTo)
          .map(([bone, n]) => `${bone}×${n}`)
          .join(', '),
      },
    });
  }
  // Chest integrity at the load door (ADR-021 Stage B): bounded clavicle +
  // in-band arm-chain influence, repaired weights REPLACING the source weights
  // in place — before hands/recenters, after the orphan rescue, so every
  // downstream consumer (export included) sees one truth. §7: refused ≠
  // not-needed ≠ repaired ≠ out-of-class, every outcome a named marker.
  if (doc.target.chestRepair?.optOut === true) {
    // ADR-021 §7: a reasoned, human-declared opt-out — the costume/mech door.
    // Logged as evidence; never a silent skip.
    bindMarkers.push({
      code: 'clavicle-influence-opted-out',
      detail: { reason: doc.target.chestRepair.reason },
    });
  } else {
    const chest = applyChestRepair(loaded.scene, rig, deps.ontology);
    if (chest.resolution.kind === 'not-applicable') {
      bindMarkers.push({
        code: 'clavicle-influence-not-applicable',
        detail: { missing: chest.resolution.missing.join(', ') },
      });
    } else {
      for (const m of chest.meshes) {
        if (m.outcome.kind === 'repaired') {
          const r = m.outcome.report;
          bindMarkers.push({
            code: 'clavicle-influence-bounded',
            detail: {
              mesh: m.mesh,
              culledMassPct: Number((100 * r.culledMassFraction).toFixed(2)),
              donorFlows: r.donorFlows.map((f) => `${f.rigBone}×${f.mass.toFixed(1)}`).join(', '),
              bounds: r.sides
                .map((s) => `${s.side} ${s.bound.toFixed(3)} (${s.culledMass.toFixed(1)} culled, arm ${s.armChainCulledMass.toFixed(1)})`)
                .join('; '),
              droppedSlotMass: Number(r.droppedSlotMass.toFixed(4)),
            },
          });
        } else if (m.outcome.kind === 'refused') {
          bindMarkers.push({
            code: 'clavicle-influence-refused',
            detail: { mesh: m.mesh, reasons: m.outcome.reasons.join('; ') },
          });
        } else {
          // 'not-needed' (measured clean) and 'no-armed-weight' (face/hair
          // meshes) — a clean pass must never look like a silent skip.
          bindMarkers.push({
            code: 'clavicle-influence-not-needed',
            detail: { mesh: m.mesh, why: m.outcome.kind },
          });
        }
      }
    }
  }
  /** Replaced hands' native digit BONE reach, measured before the strip (the isolation sizes by it). */
  const replacedDigitReach = new Map<string, number>();
  if (doc.target.hands.length > 0) {
    const mesh = soleSkinnedMesh(loaded.scene);
    for (const hand of doc.target.hands) {
      // ---- REPLACE FINGERS (2026-09-29) — FIRST, before anything reads the hand: the
      //      hand's mapped digit chains leave the rig and the scene, their flesh folded
      //      into the hand (rest mesh unchanged), so what follows sees a fingerless hand
      //      and grafts from the pins exactly as it would on one.
      if (hand.nativeDigits === 'replace') {
        let stripped;
        try {
          stripped = stripDigitsFromRig(rig, hand.handBone);
        } catch (err) {
          if (err instanceof StripDigitsRefusal) throw new DocumentRefusal(`hand "${hand.handBone}": ${err.message}`);
          throw err;
        }
        // Synthesized tips (`…4_synth`, ADR-010 §12) are rig-only until materialization: the
        // rig loses them all, the scene only the bones it has.
        const inScene = stripped.removed.filter((n) => loaded.scene.getObjectByName(n) !== undefined);
        // The old digits' BONE reach, taken before they go: the graft's isolation below sizes the
        // hand by it (HAND_REACH_DIGITS) — a replaced hand whose weights lie (Daria's wing) would
        // otherwise isolate half a metre of membrane as hand, and hand it to the new fingers.
        const wristBind = rig.bones.find((b) => b.name === hand.handBone)?.worldBind.position;
        if (wristBind !== undefined) {
          let reach = 0;
          for (const name of stripped.removed) {
            const p = rig.bones.find((b) => b.name === name)?.worldBind.position;
            if (p !== undefined) reach = Math.max(reach, Math.hypot(p.x - wristBind.x, p.y - wristBind.y, p.z - wristBind.z));
          }
          if (reach > 0) replacedDigitReach.set(hand.handBone, reach);
        }
        const report = stripBonesFromSkin(loaded.scene, inScene, hand.handBone);
        rig = stripped.rig;
        bindMarkers.push({
          code: 'native-digits-replaced',
          detail: { hand: hand.handBone, chains: stripped.chains.length, bones: stripped.removed.length, verticesFolded: report.verticesFolded },
        });
      }
      // Pinned tips, when present, ARE the answer: they carry their own identity,
      // so no thumb pin is required and no profile is cross-checked (the count is
      // however many digits were pinned). Falling back to the thumb branch keeps
      // every Document written before pins existed working unchanged.
      const pinned = digitPinsFor(doc.target, hand);
      const thumb = pinned === null ? requiredConfirmation(doc.target, hand, 'thumbBranch') : null;
      const declared = pinned === null ? optionalConfirmation(doc.target, hand, 'handProfile') : undefined;
      const handBone = findBoneByName(mesh, hand.handBone);
      if (!handBone) {
        throw new DocumentRefusal(
          `hand "${hand.handBone}": no such bone in "${doc.target.label}" — the Document names a hand ` +
            `the rig does not have (§8: fails at load, loudly, naming it).`,
        );
      }
      const forearm = hand.forearmBone === null ? null : findBoneByName(mesh, hand.forearmBone);
      if (hand.forearmBone !== null && !forearm) {
        throw new DocumentRefusal(`hand "${hand.handBone}": forearm bone "${hand.forearmBone}" not found in "${doc.target.label}".`);
      }
      // ---- wristStation (ADR-025) — BEFORE isolation, because everything after
      //      it hangs off the hand joint: the cut plane, the wrist centre, the
      //      knuckle row, the retained segment, the thumb CMC. Tripo drops the
      //      joint into the palm on some characters (Kara, Lore, Darazin — the
      //      field, 2026-08-15); a person's click on the wrist is the answer.
      //      Only the click's station ALONG the arm moves the joint here — the
      //      lateral seat stays wristRecenter's job and runs after, about the
      //      moved joint. Then the palm root: flesh the severed forearm chain
      //      owns distal of the station, within the hand's reach, is re-owned
      //      into the isolation (D2). Absent: nothing moves, nothing is re-owned
      //      (D4 — bit-identity for every hand already working).
      const stationAnswer = optionalConfirmation(doc.target, hand, 'wristStation');
      let owned: ReadonlySet<number> = new Set<number>();
      if (stationAnswer !== undefined) {
        if (forearm === null || hand.forearmBone === null) {
          throw new DocumentRefusal(
            `hand "${hand.handBone}": a wristStation is a place ALONG THE ARM and this hand declares no forearm bone — ` +
              `declare the forearm, or remove the station (§8: refuse, never guess an axis).`,
          );
        }
        const stationPin = parseDigitPin(stationAnswer.value)!; // schema-guaranteed
        const jointBind = rig.bones.find((b) => b.name === hand.handBone)!.worldBind.position;
        const forearmBind = rig.bones.find((b) => b.name === hand.forearmBone)!.worldBind.position;
        const axis = { x: jointBind.x - forearmBind.x, y: jointBind.y - forearmBind.y, z: jointBind.z - forearmBind.z };
        const limbLength = Math.hypot(axis.x, axis.y, axis.z);
        // The hand's own reach: the farthest vertex the hand subtree dominates,
        // from the JOINT. Bounds the station's perpendicular tolerance (a click
        // is on the skin — inside reach; a stale coordinate from another model
        // is not — Codex P2, #97) and the re-ownership (a cape or a holstered
        // prop weighted to the forearm never becomes a hand).
        const handSubtree = new Set<number>();
        handBone.traverse((o) => {
          const i = mesh.skeleton.bones.indexOf(o as (typeof mesh.skeleton.bones)[number]);
          if (i >= 0) handSubtree.add(i);
        });
        const ownership = bindWorldOwnership(mesh);
        let reach = 0;
        for (const v of ownership) {
          if (!handSubtree.has(v.dominantBone)) continue;
          reach = Math.max(reach, Math.hypot(v.position.x - jointBind.x, v.position.y - jointBind.y, v.position.z - jointBind.z));
        }
        if (!(reach > 0)) {
          throw new DocumentRefusal(
            `hand "${hand.handBone}": no vertex is dominated by the hand subtree, so the hand's reach cannot be measured — ` +
              `a wrist station cannot be bounded (§8: refuse, never widen the tolerance to guess).`,
          );
        }
        let move;
        try {
          move = wristStationMove(jointBind, axis, limbLength, stationPin.tip, reach);
        } catch (err) {
          throw new DocumentRefusal(`hand "${hand.handBone}": ${err instanceof Error ? err.message : String(err)}`);
        }
        // THE PIN SAYS WHERE ACROSS THE WRIST, TOO (Allen, 2026-08-26). The
        // click is a surface point, so the joint cannot sit ON it — but its
        // lateral position is the person's answer, and the elbow-sized
        // cylinder capture that used to seat the joint laterally swallowed
        // the palm and the thumb (0.45 × forearm in radius) and read the
        // centroid of hand mass: the joint landed over the thumb. The seat is
        // BENEATH the click at the tube's mid-depth — two coordinates honoured,
        // the third measured — and it supersedes wristRecenter's lateral seat.
        let seatPosition = move.position;
        let seatDepth: number | null = null;
        try {
          // The seat section: ±8% of the forearm about the station's plane (a
          // 120 mm forearm → a 19 mm slab) and a 20% link for the ring's
          // connected cluster — both measured on a low-poly generated wrist,
          // both named at their declaration (Codex P1, #111). The chord
          // tolerance is the SECTION's own, 30% of its half-width.
          const seat = wristSeatUnderPin(bindPoseVertices(loaded.scene), move.position, axis, stationPin.tip, {
            slabHalf: STATION_SEAT_SLAB_HALF_FRACTION * limbLength,
            linkDistance: STATION_SEAT_LINK_FRACTION * limbLength,
          });
          seatPosition = seat.position;
          seatDepth = seat.depth;
        } catch (err) {
          throw new DocumentRefusal(`hand "${hand.handBone}": ${err instanceof Error ? err.message : String(err)}`);
        }
        recenterSceneBoneBind(mesh, hand.handBone, seatPosition);
        rig = recenterBoneBind(rig, hand.handBone, seatPosition);
        owned = reownedVertices(ownership, move.position, axis, limbLength, severedForearmChain(mesh, forearm, handBone), reach * 1.25);
        bindMarkers.push({
          code: 'wrist-station',
          detail: {
            hand: hand.handBone,
            alongFraction: Number(move.alongFraction.toFixed(4)),
            movedWorldUnits: Number(move.along.toFixed(4)),
            seat: 'under-pin',
            seatDepthWorldUnits: Number((seatDepth ?? 0).toFixed(4)),
            reowned: owned.size,
          },
        });
      }
      const replacedReach = replacedDigitReach.get(hand.handBone);
      const iso = isolateHandRegion(mesh, handBone, forearm, { owned, ...(replacedReach === undefined ? {} : { digitReach: replacedReach }) });
      const grid = voxelizeMesh(iso.positions, iso.indices, {
        cutPlane: iso.cutPlane,
        resolution: hand.resolution.value,
      });
      // parseDocument guarantees membership in HAND_PROFILE_VALUES, which is
      // `satisfies`-bound to SeparationProfile — the cast cannot go stale.
      const analysis = analyzeSeparationProfile(grid, {
        // Under an assignment the thumb is one named digit among five, so there is
        // no separate pin to feed the analyzer — identity arrives after measurement.
        ...(thumb !== null ? { confirmedThumbBranchId: thumb.value } : {}),
        ...(declared !== undefined ? { declaredProfile: declared.value as SeparationProfile } : {}),
      });
      if (analysis.kind !== 'hand') {
        const why = analysis.kind === 'rejected' ? ` (${analysis.reason})` : '';
        const stationHint =
          stationAnswer === undefined
            ? ''
            : ` This hand carries a wrist station (${owned.size} vertices re-owned) — if the station sits too far up the arm the volume swallows forearm; move the wrist mark closer to the crease, or remove it.`;
        throw new DocumentRefusal(
          `hand "${hand.handBone}" @ res ${hand.resolution.value}: separation analysis returned ` +
            `'${analysis.kind}'${why} — nothing graftable here; re-voxelize or correct the hand bone.${stationHint}`,
        );
      }
      // ---- wristRecenter (unbind-adjust-rebind #1, 2026-07-28). Generated rigs
      //      put the wrist joint in FRONT of the wrist flesh; a hand rotating
      //      about that point orbits instead of hinging. The confirmed method
      //      'seed-centroid' moves the bone's bind to the wrist cut's centre —
      //      rest+IBM together on the scene, the same surgery on the descriptor
      //      — so the mesh does not move and only the pivot does. BEFORE the
      //      graft, so the digits' local binds compose against the moved hand.
      const recenter = optionalConfirmation(doc.target, hand, 'wristRecenter');
      // The method is validated BEFORE any branch — a station must not let an
      // undesigned method slip past the loud refusal (Codex P2, #111).
      if (recenter !== undefined && recenter.value !== 'seed-centroid') {
        throw new DocumentRefusal(
          `hand "${hand.handBone}": wristRecenter method "${recenter.value}" is not designed — ` +
            `only 'seed-centroid' exists (§8: refuse, never ignore).`,
        );
      }
      if (recenter !== undefined && stationAnswer !== undefined) {
        // A declared station already seated the joint beneath the pin; the
        // cylinder capture would move it back to the hand-mass centroid.
        // Superseded, and SAID (the stored 'seed-centroid' answer must not rot).
        bindMarkers.push({
          code: 'wrist-recentered',
          detail: { hand: hand.handBone, movedWorldUnits: 0, method: 'seed-centroid', instrument: 'superseded-by-station', instrumentEvidence: 0 },
        });
      } else if (recenter !== undefined) {
        // The pivot target is the RAW-VERTS flesh centre at the wrist station
        // (the elbow recenter's limb-axis capture, Allen-eye-validated) — NOT
        // any isolation-derived middle. Measured on the fox pair, 2026-08-02:
        // skin weights near the wrist are authoring noise, so the hand-weighted
        // region can occupy only the anterior third of the visible tube and
        // every voxel-solid middle inherits the bias (L pivot read centred by
        // the isolation and sat 2 cm forward of the tube the eye judges).
        // The whole-mesh cylinder capture reads the tube itself. A hand with
        // no forearm falls back to the voxel-slab centre, named in the marker.
        // The confirmation vocabulary keeps 'seed-centroid' (a stored Document
        // must not rot); `instrument` in the marker says what actually ran.
        const before = rig.bones.find((b) => b.name === hand.handBone)!.worldBind.position;
        const forearmB = hand.forearmBone === null ? undefined : rig.bones.find((b) => b.name === hand.forearmBone);
        let centre: { x: number; y: number; z: number };
        let instrument: string;
        let instrumentEvidence: number;
        try {
          if (forearmB === undefined) throw new DocumentRefusal('no forearm — voxel fallback');
          const fp = forearmB.worldBind.position;
          const axis = { x: before.x - fp.x, y: before.y - fp.y, z: before.z - fp.z };
          const len = Math.hypot(axis.x, axis.y, axis.z);
          const la = limbAxisCentre(
            bindPoseVertices(loaded.scene),
            before,
            { x: axis.x / len, y: axis.y / len, z: axis.z / len },
            len,
          );
          centre = la.centre;
          instrument = 'limb-axis';
          instrumentEvidence = la.sampled;
        } catch {
          // A starved capture degrades to the voxel-slab centre — the marker
          // names which instrument actually ran, so a fallback is evidence,
          // never a silent substitution.
          const cut = robustCutCentre(grid, iso.cutPlane);
          centre = voxelPointToWorld(grid, cut.centre);
          instrument = 'voxel-slab';
          instrumentEvidence = cut.slabMass;
        }
        const movedBy = Math.hypot(centre.x - before.x, centre.y - before.y, centre.z - before.z);
        recenterSceneBoneBind(mesh, hand.handBone, centre);
        rig = recenterBoneBind(rig, hand.handBone, centre);
        bindMarkers.push({
          code: 'wrist-recentered',
          detail: {
            hand: hand.handBone,
            movedWorldUnits: Number(movedBy.toFixed(4)),
            method: 'seed-centroid',
            instrument,
            instrumentEvidence,
          },
        });
      }
      // THE ANALYZER'S EVIDENCE BECOMES THE RUN'S EVIDENCE (ADR-022 §3.3, §9).
      // Placement no longer REFUSES on a declaration the volume contradicts —
      // it cannot, because pins carry no declaration, and it no longer needs to,
      // because you can only place a digit somebody pinned: four pins make four
      // digits whatever the profile chip claims, so the fabrication the old
      // refusal prevented is now structurally impossible. But a contradiction is
      // still a thing a person must be able to SEE, so what was a gate becomes a
      // witness here rather than disappearing — every analyzer marker for this
      // hand, contradiction included, joins the run's evidence stream.
      for (const marker of analysis.evidence.markers) {
        bindMarkers.push({ ...marker, detail: { ...marker.detail, hand: hand.handBone } });
      }

      // ---- PINS → placement (ADR-022 T2) ------------------------------------
      // The Document now stores digit targets as BIND-WORLD TIPS, so a hand's
      // digits are no longer required to co-separate at one resolution — the
      // resolution below governs only what the volume can WITNESS, never what
      // the person is allowed to have meant.
      //
      // Two ways in, and both end as pins:
      //  · pinned — the tips a person placed, carried in the Document. The
      //    analysis witnesses them (`witnessPins`) and lends the thumb its chain.
      //  · unpinned — no digit confirmations, only a thumb branch: the naming
      //    PROPOSAL runs against this analysis and its branch tips become the
      //    pins. This is the pre-ADR-022 path, kept for Documents that were
      //    written that way.
      //
      // AN UNCONFIDENT PROPOSAL REFUSES AN UNATTENDED RUN (Codex P1, #90):
      // a proposal answers softly by design — an over-count returns the thumb
      // alone, a coin-flip ordering returns names it does not stand behind —
      // and a Document run has no person to read those flags. §8's rule.
      const wristCentre = robustCutCentre(grid, iso.cutPlane).centre;
      let handPins: DigitPin[];
      if (pinned !== null) {
        handPins = witnessPins(pinned, analysis.branches, grid, wristCentre, hand.handBone, bindMarkers);
      } else {
        const proposal = proposeDigitNames(analysis.branches, { thumbBranchId: thumb!.value });
        if (!proposal.confident || Object.keys(proposal.assignment).length === 0) {
          const why = proposal.markers.map((m) => m.code).join(', ') || 'no naming evidence';
          throw new DocumentRefusal(
            `hand "${hand.handBone}": no digit pins, and the thumb pin "${thumb!.value}" could not ` +
              `anchor a confident naming proposal at resolution ${hand.resolution.value} (${why}) — pin ` +
              `the digits explicitly, or move the resolution to one where the volume separates them.`,
          );
        }
        handPins = pinsFromBranches(analysis.branches, proposal.assignment);
      }
      // wristCentre (above): the ROBUST wrist-cut centre, not the host hand bone
      // (it sits forward of the palm on some generated rigs) and not the raw seed
      // scrap (it can leak off the flesh at a ragged cut — foxDude 2026-08-02,
      // and the knuckle row would inherit the error).
      //
      // handAttach: where the grafted chain will actually HANG (post-recenter,
      // so after a wrist recenter it IS the wrist). The thumb's CMC seats here
      // — a CMC at the wrist-CUT centre gave Eric's thumb two bones to the
      // knuckle and a palm-sweeping metacarpal that stole the middle finger's
      // flesh (field, 2026-08-14).
      const attachWorld = rig.bones.find((b) => b.name === hand.handBone)?.worldBind.position;
      const placed = placeDigits(handPins, {
        side: hand.side,
        wristCentre,
        grid,
        ...(attachWorld === undefined ? {} : { handAttach: worldPointToVoxel(grid, attachWorld) }),
      });
      // The thumb's opposition (field 2026-08-15): per hand, dialled on the
      // panel; absent → the kernel's anatomical default.
      const opposition = optionalConfirmation(doc.target, hand, 'thumbOpposition');
      const frames = authorHandFrames(placed, grid, {
        ...(opposition === undefined ? {} : { thumbOppositionDeg: Number(opposition.value) }),
      });
      const grafted = graftDigits(rig, placed, frames, grid, {
        family: doc.target.family,
        handRigBone: hand.handBone,
      });
      // THE GRAFT'S OWN EVIDENCE JOINS THE STREAM TOO (Codex P2, #97 round
      // eight): `graftDigits` aggregates the placement, frame-authoring and
      // graft markers — digit-chord-escapes, hand-class, thumb-frame-
      // opposition(-unmeasured), graft-incomplete-digit — and this path
      // dropped them, so a Document run, the console probe and the report
      // stayed silent about findings the kernel had already spoken. Same
      // stamp as the analyzer's markers above: a law needs every door.
      for (const marker of grafted.markers) {
        bindMarkers.push({ ...marker, detail: { ...marker.detail, hand: hand.handBone } });
      }
      // ---- Bind (ADR-010 §2 ④). The grafted bones are in the DESCRIPTOR now, but a
      //      SkinnedMesh deforms through skeleton.bones + skinIndex/skinWeight, so
      //      without this the fingers rotate and the hand stays a paddle. Weights are
      //      decided here, while the grid and the placement are still in hand; they
      //      are SEATED after materializeMissingBones, because the bones need scene
      //      nodes before the skeleton can skin through them.
      const segments: BoneSegment[] = [];
      for (const entry of grafted.grafted) {
        const digit = placed.digits.find((d) => d.name === entry.name);
        if (digit === undefined) continue; // graft skipped it; markers already say so
        // Bones 1–3 are the articulating phalanges (mcp→pip→dip→tip). The …4 tip is
        // a leaf marker with no segment — the distal bone already covers that flesh.
        for (let k = 0; k < 3; k += 1) {
          segments.push({
            boneId: entry.bones[k]!,
            from: digit.joints[k]!.position,
            to: digit.joints[k + 1]!.position,
          });
        }
      }
      const toVoxel = (p: { x: number; y: number; z: number }) => ({
        x: (p.x - grid.origin![0]!) / grid.voxelSize - 0.5,
        y: (p.y - grid.origin![1]!) / grid.voxelSize - 0.5,
        z: (p.z - grid.origin![2]!) / grid.voxelSize - 0.5,
      });
      // The rig's OWN bones compete for weight but are never rewritten. Without them
      // the palm would be normalised across knuckles alone and every palm vertex
      // would follow whichever finger happened to be nearest (bind-weights header).
      const handBind = rig.bones.find((b) => b.name === hand.handBone)?.worldBind.position;
      // The palm point is the FINGER knuckle row's mean. The thumb's first joint
      // is its CMC — since §13.2 that IS the hand attach — so including it
      // dragged the palm point back toward the hand origin and shortened the
      // retained segment by a fifth, handing that palm to the thumb (2026-08-15).
      // A thumb-only graft falls back to the thumb's MCP so the segment still
      // has a length.
      const mcps =
        segments.length > 0
          ? placed.digits.filter((d) => d.name !== 'Thumb').map((d) => d.joints[0]!.position)
          : [];
      if (segments.length > 0 && mcps.length === 0) {
        for (const d of placed.digits) if (d.name === 'Thumb') mcps.push(d.joints[1]!.position);
      }
      if (handBind !== undefined && mcps.length > 0) {
        // THE RETAIN FAN (2026-08-20, ruled from Allen's field read + the
        // overlay census on Kara): ONE segment to the MEAN knuckle was a line
        // defending a surface. §13.2 seats the thumb's metacarpal THROUGH the
        // palm and ADR-024 starts every finger ON the knuckle row, so at p=3
        // the proximity auction handed every off-center palm voxel to a digit
        // — Thumb2 touched 205 verts on Kara's right hand while R_Hand
        // dominated 70; the palm's ownership was inverted, and the smear was
        // exactly the palmar-webbing deformation the field reported on Kara
        // and Sergio. The old mean-segment survived only because the old
        // straight-shot placement biased digit segments distally — the bind's
        // palm assumption was load-bearing on a placement error. The hand now
        // defends the palm with a segment to EACH knuckle; retained fan
        // segments share one boneId legitimately (never emitted — see
        // bind-weights' duplicate rule).
        for (const mcp of mcps) {
          segments.push({ boneId: hand.handBone, from: toVoxel(handBind), to: mcp, retain: true });
        }
        const forearmBind =
          hand.forearmBone === null ? undefined : rig.bones.find((b) => b.name === hand.forearmBone)?.worldBind.position;
        if (forearmBind !== undefined) {
          segments.push({
            boneId: hand.forearmBone!,
            from: toVoxel(forearmBind),
            to: toVoxel(handBind),
            retain: true,
          });
        }
      }
      if (segments.some((s) => s.retain !== true)) {
        // Skirt riders shape the VOLUME and keep their own weights — cuff
        // flesh re-solved among grafted finger bones is "the fingers are
        // swollen" (the `_new_v3` batch, 2026-08-15). Empty on a clean hand.
        const bindVertices: BindVertex[] = [];
        for (let i = 0; i < iso.vertexIndices.length; i += 1) {
          if (iso.volumeOnlyVertexIndices.has(iso.vertexIndices[i]!)) continue;
          bindVertices.push({
            index: iso.vertexIndices[i]!,
            position: { x: iso.positions[i * 3]!, y: iso.positions[i * 3 + 1]!, z: iso.positions[i * 3 + 2]! },
          });
        }
        const weights = computeBindWeights(grid, segments, bindVertices);
        // THE WRIST IS THE BOUNDARY AUTHORITY, NOT THE WEIGHTS (ADR-025) —
        // closed at the seat, the law's last open door. The isolation already
        // refuses to let weights define the boundary; the SEAT still preserved
        // the generator's forearm-twist paint inside the hand at
        // `1 − digitShare` — and once the palm retain fan returned the palm to
        // the retained pool, that paint was most of what the pool held.
        // Measured (Sergio's field Document, 2026-08-20): R_ForearmTwist02
        // dominated 73 hand-region vertices against the hand bone's 82 — the
        // field's "crossover leak from the wrist bones". Flesh distal of the
        // wrist plane hands its forearm-chain weight to the hand bone; the
        // factor ramps over one voxel at the cut so pronation blends across
        // the seam instead of creasing at it. Skirt riders are already absent
        // from bindVertices, so cuff flesh keeps its weights (the swollen-
        // fingers lesson).
        // The candidate set is computed AT THE SEAT over the whole mesh (see
        // WristReown): the vertices this law exists for are twist-DOMINATED,
        // so the dominant-weight isolation region never contained them — a
        // region-scoped pass measured as a no-op on exactly the 73 vertices
        // it was built to cure. Locality is the hand's own MEASURED reach
        // (max region-vertex distance from the cut point): the plane is
        // infinite, the wrist is not.
        // GATED ON THE CONFIRMED STATION (Codex P1, #99 — ADR-025 D4): with no
        // wrist answer the Document behaves exactly as before, bit-identical —
        // the boundary the law trusts is the HUMAN's confirmed wrist, never
        // the derived cut alone. One click on the crease buys the transfer.
        let reown: WristReown | undefined;
        if (hand.forearmBone !== null && stationAnswer !== undefined) {
          const children = new Map<string, string[]>();
          for (const b of rig.bones) {
            if (b.parent === null) continue;
            const list = children.get(b.parent) ?? [];
            list.push(b.name);
            children.set(b.parent, list);
          }
          const chain: string[] = [];
          const queue = [hand.forearmBone];
          while (queue.length > 0) {
            const n = queue.shift()!;
            if (n === hand.handBone) continue; // the hand subtree is not the severed chain
            chain.push(n);
            for (const c of children.get(n) ?? []) queue.push(c);
          }
          const [px, py, pz] = iso.cutPlane.point;
          let reach2 = 0;
          for (let i = 0; i < iso.positions.length; i += 3) {
            const dx = iso.positions[i]! - px;
            const dy = iso.positions[i + 1]! - py;
            const dz = iso.positions[i + 2]! - pz;
            const d2 = dx * dx + dy * dy + dz * dz;
            if (d2 > reach2) reach2 = d2;
          }
          if (chain.length > 0 && reach2 > 0) {
            reown = {
              handBone: hand.handBone,
              chainBoneNames: chain,
              plane: iso.cutPlane,
              // A hair of margin: the region's farthest vertex IS hand flesh.
              radiusWorld: Math.sqrt(reach2) * 1.05,
              bandWorld: grid.voxelSize, // one voxel of seam blend
              excludeVertexIndices: iso.volumeOnlyVertexIndices,
            };
          }
        }
        pendingBinds.push({
          handBone: hand.handBone,
          boneNames: grafted.grafted.flatMap((g) => g.bones),
          influences: weights.influences,
          ...(reown === undefined ? {} : { reown }),
        });
        bindMarkers.push(...weights.markers);
      }

      rig = grafted.rig;
      jointFrames.push(...grafted.jointFrames);
      handGrafts.push({
        handBone: hand.handBone,
        measuredProfile: analysis.profile,
        confidence: analysis.confidence,
        digits: grafted.grafted.map((g) => g.name),
        graftedBoneCount: grafted.mappingAdditions.length,
        jointFrameCount: grafted.jointFrames.length,
      });
    }
  }

  // ---- Tails (ADR-031 §3.3a, T2). AFTER the hands (ruled D2): the same
  //      materialize-then-bind order — the chain joins the rig here, its
  //      scene nodes grow with every other grafted bone below, and its
  //      weights are seated by the ONE seat (`bindGraftedDigits`) right after
  //      the hands'. The chain is DERIVED every run from the zone's faces and
  //      the declaration, so a reopened Document re-places it and "more
  //      bones" is one number changing. A zone cut on another mesh refuses by
  //      name (D2) before anything is voxelized; a placement a person must
  //      correct (no junction, a pin off the flesh, a count outside the band)
  //      refuses with the kernel's own sentence.
  const tailGrafts: TailGraftRecord[] = [];
  let zoneFingerprint: MeshFingerprint | null = null;
  if ((doc.target.tails ?? []).some((t) => t.recentre !== undefined && t.recentre.degrees !== 0)) {
    const before = subjectArrays(loaded.scene);
    zoneFingerprint = fingerprintMesh(before.positions, before.indices);
  }
  for (const tail of doc.target.tails ?? []) {
    // parseDocument guarantees the zone exists and a person called it a tail.
    const zone = (doc.zones ?? []).find((z) => z.id === tail.zoneId)!;
    let arrays = subjectArrays(loaded.scene);
    const mismatch = fingerprintMismatch(zone.meshFingerprint, fingerprintMesh(arrays.positions, arrays.indices));
    if (mismatch !== null) {
      throw new DocumentRefusal(`tail on zone "${zone.name}" refused: the zone was ${mismatch} — pin the tail again on this character (ADR-031 D2).`);
    }
    const pelvisBone = tailParentBone(rig);
    if (pelvisBone === null) {
      throw new DocumentRefusal(`tail on zone "${zone.name}" refused: "${doc.target.label}" maps no pelvis (Hips) for the chain to hang from.`);
    }
    let placed: ReturnType<typeof placeTail>;
    let grafted: ReturnType<typeof graftTail>;
    let seam = 0;
    // CENTRE THE TAIL (2026-09-24): the declared yaw turns the tail's REST
    // flesh about the vertical through its root BEFORE the chain is placed —
    // replayed from the Document on every run, never re-measured — so the
    // chain, the bind and the springs all rest centred. Blended by the tail's
    // own ownership (the junction ring stays), stopped short of a thigh.
    if (tail.recentre !== undefined && tail.recentre.degrees !== 0) {
      try {
        const placed0 = placeTail({
          mesh: arrays,
          faces: zone.faces,
          zoneName: zone.name,
          zoneKind: zone.shape === undefined ? 'tail' : `${zone.shape}, called a tail`,
          ...(zone.shape === 'stub' ? { stub: true } : {}),
          ...tailShapeOptions(zone, arrays.positions),
          count: tail.count.value,
          ...(tail.resolution !== undefined ? { resolution: tail.resolution.value } : {}),
          ...(tail.root !== undefined ? { root: tail.root } : {}),
          ...(tail.tip !== undefined ? { tip: tail.tip } : {}),
          ...(tail.fan === 'grouped' ? { fan: 'grouped' as const } : {}),
        });
        const names = Array.from({ length: placed0.stations.count.value }, (_, k) => `__recentre${k}`);
        const bind0 = tailBindInfluences(placed0, arrays.positions, names, arrays.indices);
        const factors = new Map<number, number>();
        for (const inf of bind0.influences) factors.set(inf.vertexIndex, (factors.get(inf.vertexIndex) ?? 0) + inf.weight);
        const rec = recentreTail({
          positions: arrays.positions,
          factors,
          root: placed0.isolated.root,
          degrees: tail.recentre.degrees,
          legs: legCapsules(rig, arrays),
        });
        const yaw = new Map<number, number>();
        for (const [v, f] of factors) if (f > 0) yaw.set(v, (f * rec.appliedDeg * Math.PI) / 180);
        writeSubjectBindPositions(loaded.scene, rec.positions, yaw);
        arrays = subjectArrays(loaded.scene);
        bindMarkers.push({
          code: 'tail-recentred',
          detail: {
            zone: zone.name,
            declaredDeg: Number(rec.declaredDeg.toFixed(1)),
            appliedDeg: Number(rec.appliedDeg.toFixed(1)),
            verticesMoved: rec.verticesMoved,
            stoppedBy: rec.stoppedBy ?? 'none',
          },
        });
      } catch (err) {
        if (err instanceof TailRefusal) throw new DocumentRefusal(`tail refused — ${err.message}`);
        throw err;
      }
    }
    try {
      placed = placeTail({
        mesh: arrays,
        faces: zone.faces,
        zoneName: zone.name,
        zoneKind: zone.shape === undefined ? 'tail' : `${zone.shape}, called a tail`,
        ...(zone.shape === 'stub' ? { stub: true } : {}),
        ...tailShapeOptions(zone, arrays.positions),
        count: tail.count.value,
        ...(tail.resolution !== undefined ? { resolution: tail.resolution.value } : {}),
        ...(tail.root !== undefined ? { root: tail.root } : {}),
        ...(tail.tip !== undefined ? { tip: tail.tip } : {}),
        ...(tail.fan === 'grouped' ? { fan: 'grouped' as const } : {}),
      });
      seam = tail.pivot === undefined ? 0 : tailPivotSeam(placed, tail.pivot);
      grafted = graftTail(rig, placed, { pelvisBone, ...(tail.pivot !== undefined ? { rootAt: tail.pivot } : {}) });
    } catch (err) {
      if (err instanceof TailRefusal) throw new DocumentRefusal(`tail refused — ${err.message}`);
      throw err;
    }
    const bind = tailBindInfluences(placed, arrays.positions, grafted.bones, arrays.indices, seam > 0 ? { seam } : {});
    pendingBinds.push({ handBone: `tail:${zone.name}`, boneNames: [...grafted.bones], influences: bind.influences });
    bindMarkers.push(...placed.markers, ...bind.markers, {
      code: 'tail-grafted',
      detail: {
        zone: zone.name,
        bones: grafted.bones.length,
        count: `${tail.count.value} (${tail.count.provenance})`,
        lengthCm: Number((placed.stations.length * placed.isolated.grid.voxelSize * 100).toFixed(1)),
        owned: bind.verticesOwned,
        blended: bind.verticesBlended,
        retained: bind.verticesRetained,
      },
    });
    rig = grafted.rig;
    tailGrafts.push({
      zoneId: zone.id,
      zone: zone.name,
      bones: grafted.bones,
      count: tail.count,
      defaultCount: placed.stations.defaultCount,
      joints: tail.pivot !== undefined ? [tail.pivot, ...placed.jointsWorld.slice(1)] : placed.jointsWorld,
      measuredRoot: placed.jointsWorld[0]!,
      pivotSeam: seam,
      // A placed pivot moves joint 0, so the first bone's length is the pivot's to joint 1 (Codex P2, #253);
      // `length` stays the measured walk root → tip.
      segmentLengths:
        tail.pivot !== undefined
          ? [
              Math.hypot(placed.jointsWorld[1]!.x - tail.pivot.x, placed.jointsWorld[1]!.y - tail.pivot.y, placed.jointsWorld[1]!.z - tail.pivot.z),
              ...placed.segmentLengthsWorld.slice(1),
            ]
          : placed.segmentLengthsWorld,
      length: placed.stations.length * placed.isolated.grid.voxelSize,
      meanRadius: placed.stations.meanRadius * placed.isolated.grid.voxelSize,
      radii: placed.stations.radii.map((r) => r * placed.isolated.grid.voxelSize),
      cut: placed.isolated.cutPlane,
      voxelSize: placed.isolated.grid.voxelSize,
      verticesOwned: bind.verticesOwned,
      verticesBlended: bind.verticesBlended,
      verticesRetained: bind.verticesRetained,
      dominance: bind.dominance,
    });
  }

  // ---- Tier-2 joint recentres (target.recenters).
  //
  // AFTER the hand work on purpose. The elbow is an ANCESTOR of the hand, so
  // moving it re-frames everything below — but `recenterBoneBind` preserves
  // every child's world bind, so a graft that already landed keeps its exact
  // placement. Running this first would be equally correct and needlessly
  // harder to reason about; running it last keeps one order to hold in mind.
  const selection = { label: doc.target.label, ...(doc.target.selectSkeleton ? { selectSkeleton: doc.target.selectSkeleton } : {}) };
  const rebuiltDrivers = new Set<string>();
  const confirmedElbows = new Set<string>();
  for (const mapping of rig.mapping.filter(m => m.ontologyId === 'LeftForeArm' || m.ontologyId === 'RightForeArm')) {
    if (readSceneElbowPlacement(loaded.scene, mapping.rigBone, selection)) confirmedElbows.add(mapping.rigBone);
  }
  /**
   * 'cap-height': measure where the Upperarm joint sits in its shoulder cap
   * (kernel `shoulderCapSeat`), raise it along world up to the band, hold
   * every child in world, recompute the IBM, and DECLARE it on the node
   * (extras.poqpoq.shoulderRecenter). A file that already declares it is
   * ADOPTED, never raised twice (ADR-030 §14) — unless a person authored an
   * offset, which is then applied on top and accumulated in the marker.
   * Returns the moved rig, or null when adopted.
   */
  const recenterShoulderCapHeight = (
    declared: NonNullable<TargetSession['recenters']>[number],
    bone: RigDescriptor['bones'][number],
  ): RigDescriptor | null => {
    const where = `recenter of "${declared.bone}" (cap-height)`;
    const ontologyId = rig.mapping.find((m) => m.rigBone === declared.bone)?.ontologyId;
    if (ontologyId !== 'LeftArm' && ontologyId !== 'RightArm') {
      throw new DocumentRefusal(
        `${where}: the bone maps to ${ontologyId === undefined ? 'nothing' : `"${ontologyId}"`} — ` +
          `cap-height is the SHOULDER method and needs the LeftArm/RightArm (Upperarm) bone.`,
      );
    }
    if (declared.rebuildHinge) {
      throw new DocumentRefusal(`${where}: rebuildHinge is the elbow's contract — a shoulder has no hinge to rebuild.`);
    }
    const side = ontologyId === 'LeftArm' ? 'Left' : 'Right';
    const forearm = rig.mapping.find((m) => m.ontologyId === `${side}ForeArm`)?.rigBone;
    const elbow = forearm === undefined ? undefined : rig.bones.find((b) => b.name === forearm);
    if (forearm === undefined || elbow === undefined) {
      throw new DocumentRefusal(`${where}: this rig does not map ${side}ForeArm, so the upper arm has no far end.`);
    }
    const prior = readSceneShoulderRecenter(loaded.scene, declared.bone, selection);
    if (prior !== null && declared.authoredOffset === undefined) {
      bindMarkers.push({
        code: 'shoulder-recenter-adopted',
        detail: { bone: declared.bone, provenance: prior.provenance, fractionBefore: prior.fractionBefore },
      });
      return null;
    }
    // Every skin of the SELECTED skeleton: a VRoid body is 14–20 skins on
    // one armature (Codex P2, #178), and a garment rides the body's bones.
    const skins = skinnedMeshesOfSelectedSkeleton(loaded.scene, selection);
    const jointPos = bone.worldBind.position;
    const up = rig.upAxis === 'Y' ? { x: 0, y: 1, z: 0 } : { x: 0, y: 0, z: 1 };
    let measured;
    try {
      measured = shoulderCapSeat({
        joint: jointPos,
        elbow: elbow.worldBind.position,
        up,
        armFlesh: dominantFleshOf(skins, upperArmSegmentBones(rig, declared.bone, forearm)),
        metersPerUnit: rig.units.metersPerUnit,
      });
    } catch (err) {
      if (err instanceof ShoulderCapRefusal) throw new DocumentRefusal(`${where}: ${err.reason}`);
      throw err;
    }
    // The joint as the GENERATOR seated it: a file that already carries the
    // marker has been moved by `prior.totalOffset` (bind frame; the recenter
    // never touches rotation), so the original is recovered, not assumed.
    // Every placement below is measured from THAT point, and the marker
    // records the total from it — never a sum of replays (Codex P1, #178:
    // an authored offset re-applied on each reopen used to stack).
    const original =
      prior === null ? jointPos : vec3Sub(jointPos, quatRotateVec3(bone.worldBind.rotation, prior.totalOffset));
    let centre = measured.centre;
    let provenance: 'measured' | 'authored' = 'measured';
    if (declared.authoredOffset !== undefined) {
      // A HUMAN placed it (ADR-010 §11): a fraction of the upper arm's length
      // in the joint's bind frame, from the ORIGINAL joint.
      const o = declared.authoredOffset;
      const length = Math.hypot(
        elbow.worldBind.position.x - original.x,
        elbow.worldBind.position.y - original.y,
        elbow.worldBind.position.z - original.z,
      );
      centre = vec3Add(original, quatRotateVec3(bone.worldBind.rotation, { x: o.x * length, y: o.y * length, z: o.z * length }));
      provenance = 'authored';
    }
    const total = quatRotateVec3(quatInvertUnit(bone.worldBind.rotation), vec3Sub(centre, original));
    writeSceneShoulderRecenter(
      loaded.scene,
      declared.bone,
      {
        version: 1,
        method: 'cap-height',
        provenance,
        source: doc.target.label,
        fractionBefore: prior?.fractionBefore ?? Number(measured.fraction.toFixed(4)),
        targetFraction: measured.targetFraction,
        totalOffset: total,
      },
      selection,
    );
    const rebound = recenterSceneBoneBindAcross(skins, declared.bone, centre);
    bindMarkers.push({
      code: 'joint-recentered',
      detail: {
        bone: declared.bone,
        method: 'cap-height',
        skins: rebound,
        movedWorldUnits: Number(Math.hypot(centre.x - jointPos.x, centre.y - jointPos.y, centre.z - jointPos.z).toFixed(4)),
        evidence:
          provenance === 'authored'
            ? `cap-height (${measured.sampled} verts, seat ${measured.fraction.toFixed(2)}, authored offset)`
            : measured.raise > 0
              ? `cap-height (${measured.sampled} verts, seat ${measured.fraction.toFixed(2)} → ${measured.targetFraction.toFixed(2)})`
              : `cap-height (${measured.sampled} verts, seat ${measured.fraction.toFixed(2)} already at or above ${measured.targetFraction.toFixed(2)} — not moved)`,
      },
    });
    return recenterBoneBind(rig, declared.bone, centre);
  };
  for (const declared of doc.target.recenters ?? []) {
    const bone = rig.bones.find((b) => b.name === declared.bone);
    if (bone === undefined) {
      throw new DocumentRefusal(
        `recenter names bone "${declared.bone}", which this rig does not have — ` +
          `bones: ${rig.bones.length} (§8: refuse, never silently skip).`,
      );
    }
    if (declared.method === 'cap-height') {
      // The SHOULDER recenter (2026-09-22): raise the Upperarm pivot within
      // its cap to the A-pose band. A method of its own, not limb-axis:
      // the defect is vertical placement within the cap, not a perpendicular
      // offset from the tube's centre (lateral ratio 0.75–1.02 everywhere).
      const recentered = recenterShoulderCapHeight(declared, bone);
      if (recentered !== null) {
        rig = recentered;
      }
      continue;
    }
    if (declared.method !== 'limb-axis') {
      throw new DocumentRefusal(
        `recenter of "${declared.bone}": method "${declared.method}" is not designed — ` +
          `only 'limb-axis' (elbow/wrist) and 'cap-height' (shoulder) exist (§8: refuse, never ignore).`,
      );
    }
    // The limb comes from the shared kernel resolver, so the driver and the
    // panel that proposes the move can never disagree about what it measured.
    let limb;
    try {
      limb = resolveLimb(rig, deps.ontology, declared.bone);
    } catch (err) {
      throw new DocumentRefusal(`recenter of "${declared.bone}": ${err instanceof Error ? err.message : String(err)}`);
    }
    const jointPos = limb.joint;
    const limbLength = limb.limbLength;
    let centre;
    let evidence: string;
    if (declared.authoredOffset !== undefined) {
      // A HUMAN MOVED IT. Their placement is the answer and the measurement
      // does not get a vote (ADR-010 §11). The offset is a fraction of limb
      // length in the joint's bind frame, so it survives a rescaled rig.
      const o = declared.authoredOffset;
      const world = quatRotateVec3(bone.worldBind.rotation, {
        x: o.x * limbLength,
        y: o.y * limbLength,
        z: o.z * limbLength,
      });
      centre = { x: jointPos.x + world.x, y: jointPos.y + world.y, z: jointPos.z + world.z };
      evidence = 'authored';
    } else {
      const measured = limbAxisCentre(bindPoseVertices(loaded.scene), jointPos, limb.axis, limbLength);
      centre = measured.centre;
      evidence = `limb-axis (${measured.sampled} verts)`;
    }
    const movedBy = Math.hypot(centre.x - jointPos.x, centre.y - jointPos.y, centre.z - jointPos.z);
    if (declared.rebuildHinge) {
      const family = doc.target.family === 'unknown' ? (detectRigFamily(rig) ?? 'unknown') : doc.target.family;
      const template = hingeTemplateFor(deps.priors, family);
      if (template === null) throw new DocumentRefusal(`No hinge template for ${declared.bone}`);
      const oldHinge = graftHingeHelpers(rig, template).hinges.find(h => h.driver === declared.bone);
      if (!oldHinge) throw new DocumentRefusal(`No rebuildable hinge for ${declared.bone}`);
      for (const mesh of allSkinnedMeshes(loaded.scene)) {
        if (!mesh.skeleton.bones.some(b => b.name === oldHinge.driver)) continue;
        const report = clearHingeCorrection(mesh, rig, oldHinge);
        bindMarkers.push({ code: 'hinge-paint-reconstructed', detail: { bone: declared.bone, ...report } });
      }
      const previous = readSceneElbowPlacement(loaded.scene, declared.bone, selection);
      const local = quatRotateVec3(quatInvertUnit(bone.worldBind.rotation), vec3Sub(centre, jointPos));
      writeSceneElbowPlacement(loaded.scene, declared.bone, {
        version: 1, provenance: 'explicit', source: previous?.source ?? doc.target.label,
        totalOffset: vec3Add(previous?.totalOffset ?? { x: 0, y: 0, z: 0 }, local),
      }, selection);
      confirmedElbows.add(declared.bone);
      rebuiltDrivers.add(declared.bone);
    }
    recenterSceneBoneBind(soleSkinnedMesh(loaded.scene), declared.bone, centre);
    rig = recenterBoneBind(rig, declared.bone, centre);
    bindMarkers.push({
      code: 'joint-recentered',
      detail: {
        bone: declared.bone,
        method: declared.method,
        movedWorldUnits: Number(movedBy.toFixed(4)),
        evidence,
      },
    });
  }

  // ---- Sources.
  //
  // A source is imported AT THE PROFILE IT WILL BE JUDGED AT. Tip synthesis
  // (ADR-010 §12) fabricates only the leaf bones the requested profile needs,
  // so importing at the default `minimal` and then gating the retarget at
  // `minimal+hands` refused every source that does not AUTHOR its own `…4`
  // finger ends: the bones were never synthesized, so coverage could not be
  // satisfied at any later stage. MEASURED 2026-07-30 (source-tip-gap-probe):
  // imported at `minimal` the gate refuses "LeftHandThumb4 is unmapped for
  // retarget:source"; imported at `minimal+hands` the same rig PASSES. That
  // mismatch is the bake's two dual-skeleton failures and the reason a VRM
  // could never reach `minimal+hands` — the profile is a REQUEST to the
  // importer, not a label applied afterwards.
  //
  // A source shared by selections that disagree on profile is a Document we
  // cannot serve with one import; that is named, not silently resolved.
  const selectionProfiles = new Map<string, Set<string>>();
  for (const sel of doc.clipSelections) {
    const set = selectionProfiles.get(sel.source) ?? new Set<string>();
    set.add(sel.profile);
    selectionProfiles.set(sel.source, set);
  }
  const sources = new Map<string, ImportResult | FbxImportResult | SlAnimImportResult>();
  for (const source of doc.sources) {
    const wanted = [...(selectionProfiles.get(source.label) ?? new Set([doc.target.profile]))];
    if (wanted.length > 1) {
      throw new DocumentRefusal(
        `source "${source.label}" is selected at ${wanted.length} different profiles (${wanted.join(', ')}) — ` +
          `one import cannot satisfy both, because tip synthesis is per-profile. Split the source or align the selections.`,
      );
    }
    const bytes = await loadVerified(io, source.specimen, source.label);
    const options = {
      label: source.label,
      profile: wanted[0] ?? doc.target.profile,
      ...(source.selectSkeleton !== undefined ? { selectSkeleton: source.selectSkeleton } : {}),
      ...(source.declaredFps !== undefined ? { declaredFps: source.declaredFps } : {}),
    };
    sources.set(
      source.label,
      source.specimen.format === 'glb'
        ? await importGlb(bytes, deps, options)
        : source.specimen.format === 'fbx'
          ? await importFbx(bytes, deps, options)
          : source.specimen.format === 'bvh'
            ? // BVH via Document rides the spec defaults (Y-up, metres, +Z) —
              // per-corpus convention overrides are an interactive-door
              // affordance today; a Document field can declare them when a
              // corpus needs it (fails loudly on the Z-up degenerate case).
              await importBvh(bytes, deps, options)
            : await importSlAnim(bytes, deps, options),
    );
  }

  // ---- ADR-028 E1: hinge helpers, grafted by family RULE with axes measured
  //      from THIS rig's bind (a family-constant axis was falsified by
  //      measurement — Tripo rolls bones about their long axis within the
  //      family). No template for the family = no correctives, by declaration
  //      (§7); a template naming bones the rig lacks refuses loudly inside.
  const hingeFamily = doc.target.family === 'unknown' ? (detectRigFamily(rig) ?? 'unknown') : doc.target.family;
  const hingeTemplate = hingeTemplateFor(deps.priors, hingeFamily);
  let hinges: readonly ResolvedHinge[] = [];
  // Correctives the rig already wore (a re-imported augmented export): the
  // band and belly transfers live in its paint; the doors must not run twice.
  let adoptedCorrectives = new Set<string>();
  if (hingeTemplate !== null) {
    // Compute fresh sites without adopting the old helper binds, then reseat
    // the existing scene bones. Skeleton indices remain stable; hands stay put.
    if (rebuiltDrivers.size > 0) {
      const old = graftHingeHelpers(rig, hingeTemplate).hinges.filter(h => rebuiltDrivers.has(h.driver));
      const names = new Set(old.flatMap(h => [h.helper, ...h.leaves.map(l => l.name)]));
      const fresh = graftHingeHelpers({ ...rig, bones: rig.bones.filter(b => !names.has(b.name)) }, hingeTemplate);
      for (const name of names) {
        const target = fresh.rig.bones.find(b => b.name === name);
        if (!target) throw new DocumentRefusal(`Rebuilt hinge lost ${name}`);
        if (rig.bones.some(b => b.name === name)) {
          const mesh = allSkinnedMeshes(loaded.scene).find(m => m.skeleton.bones.some(b => b.name === name));
          if (!mesh) throw new DocumentRefusal(`Rebuilt hinge has no skin for ${name}`);
          recenterSceneBoneBind(mesh, name, target.worldBind.position);
          rig = recenterBoneBind(rig, name, target.worldBind.position);
        }
      }
    }
    const grafted = graftHingeHelpers(rig, hingeTemplate);
    rig = grafted.rig;
    hinges = grafted.hinges;
    adoptedCorrectives = new Set(grafted.adopted);
    for (const h of hinges) if (rebuiltDrivers.has(h.driver)) {
      adoptedCorrectives.delete(h.helper);
      for (const leaf of h.leaves) adoptedCorrectives.delete(leaf.name);
    }
    bindMarkers.push(...grafted.markers);
    // Report only what was actually ADDED — an adopted helper is evidence of
    // the earlier graft, not this one (Codex P2, #110).
    const newlyGrafted = grafted.hinges.map((h) => h.helper).filter((h) => !adoptedCorrectives.has(h));
    if (newlyGrafted.length > 0) {
      bindMarkers.push({
        code: 'hinge-helpers-grafted',
        detail: { family: hingeFamily, helpers: newlyGrafted.join(', ') },
      });
    }
  }

  // ---- ADR-029: the pelvis owns its flesh. Resolved by family RULE against
  //      THIS rig's mapping and bind (owner = the bone the thighs hang from,
  //      station = the first lumbar joint); applied to the mesh below, BEFORE
  //      the hinge band so a hip hinge's parent holds the flesh its band
  //      reads. No declaration for the family = the paint stands as shipped.
  const pelvisDecl = pelvisOwnershipFor(deps.priors, hingeFamily);
  let pelvisOwnership: ResolvedPelvisOwnership | null = null;
  if (pelvisDecl !== null) {
    const outcome = resolvePelvisOwnership(rig, pelvisDecl);
    pelvisOwnership = outcome.resolved;
    bindMarkers.push(...outcome.markers);
  }

  // ---- Clip selections: takes are found BY NAME; a miss names what exists.
  const priors =
    jointFrames.length > 0
      ? parsePriorsBundle({ ...deps.priors, jointFrames: [...(deps.priors.jointFrames ?? []), ...jointFrames] })
      : deps.priors;
  const clips: DocumentClipRun[] = doc.clipSelections.map((selection) => {
    const source = sources.get(selection.source)!; // parseDocument guarantees the label
    const imported = source.clips.find((c) => c.clip.name === selection.take);
    if (!imported) {
      const available =
        'takes' in source
          ? source.takes.map((t) => `${t.name}[${t.classification}]`).join(', ')
          : source.clips.map((c) => c.clip.name).join(', ');
      throw new DocumentRefusal(
        `clip selection "${selection.take}" not found in source "${selection.source}" — ` +
          `available: ${available || '(none)'}. Takes are selected by name, never by position (§8).`,
      );
    }
    // The source family is detected from the source rig's own names, same
    // posture as the target's declared family: authored frames pair
    // secondaries ACROSS sides, so the source must be allowed to carry its
    // own (the Mixamo arm frames — a one-sided pairing mixes semantics).
    const sourceFamily = detectRigFamily(source.rig);
    // The TARGET falls back to the same signature detection when the Document
    // declares 'unknown' — the drop door's deliberate declaration (main.ts).
    // Without this, family-keyed authored frames (the VRM leg AND spine
    // cures) never reached a dropped file at all: the 2026-08-03 waist
    // measurement found the frames authored, parsed, and UNCONSULTED, because
    // 'unknown' matched no family key. An explicit declaration still wins —
    // detection is the fallback, never the override (signature-not-metadata).
    const targetFamily = doc.target.family === 'unknown' ? (detectRigFamily(rig) ?? 'unknown') : doc.target.family;
    const result = retargetClip(
      deps.ontology,
      { rig: sources.get(selection.source)!.rig, clip: imported.clip },
      { rig },
      {
        profile: selection.profile,
        priors,
        targetFamily,
        ...(sourceFamily !== null ? { sourceFamily } : {}),
      },
    );
    return { selection, sourceLabel: selection.source, result };
  });
  // The retarget's own refusals (a contradicted family frame, a demoted
  // spine witness pair) reach the evidence stream — one marker per verdict.
  bindMarkers.push(...retargetEvidenceMarkers(clips));

  // ---- Grow scene nodes for synthesized/grafted bones so retargeted tracks
  //      have homes when a caller presents this result (view concern, §3.3).
  const materializedBones = materializeMissingBones(loaded.scene, rig.bones);

  // ---- Seat the hand bind (ADR-010 §2 ④). Strictly after materialization: the
  //      skeleton can only skin through bones that HAVE scene nodes. This is the
  //      step whose absence made grafted hands paddles — see bind-gap-probe.
  const handBinds: HandBindRecord[] = [];
  if (pendingBinds.length > 0) {
    const mesh = soleSkinnedMesh(loaded.scene);
    for (const pending of pendingBinds) {
      const report = bindGraftedDigits(mesh, loaded.scene, rig, pending.boneNames, pending.influences, pending.reown);
      handBinds.push({
        handBone: pending.handBone,
        bonesAdded: report.bonesAdded.length,
        verticesReweighted: report.verticesReweighted,
        influencesEvicted: report.influencesEvicted,
        largestEviction: report.largestEviction,
        worstWeightSumError: report.worstWeightSumError,
      });
      // The wrist boundary law spoke — the evidence stream carries it, ZERO
      // included (Codex P2, #99): a clean hand's 0 must stay distinguishable
      // from the pass never running. Same rule as every graft marker:
      // automatic is fine, silent is not.
      if (pending.reown !== undefined) {
        bindMarkers.push({
          code: 'wrist-weights-reowned',
          detail: { hand: pending.handBone, vertices: report.verticesWristReowned, unseated: report.verticesWristUnseated, bandVoxels: 1 },
        });
      }
    }
  }

  // ---- Seat + reweight the hinge helpers (ADR-028 E1), strictly after the
  //      hand binds so the crease band reads the post-hand attributes. The
  //      seat is the ONE seat, reused with an empty influence list (§4: a
  //      smaller sibling of the finger graft, not a new mechanism); the band
  //      transfer then moves chain weight onto the seated helper.
  const pelvisReowns: PelvisReownReport[] = [];
  if (pelvisOwnership !== null) {
    // EVERY skinned mesh of the SELECTED skeleton (the same law as the band):
    // body and garments both carry spine paint below the station. Identity is
    // the import's own selection rule re-applied to THIS scene (the run
    // parses the file twice, so uuids never cross; names never decide — a
    // multi-skeleton target's unselected skeleton can carry the very same
    // names, Codex P2 #108); a door keyed on names would re-own a foreign
    // skin with this rig's station.
    const selectedList = skinnedMeshesOfSelectedSkeleton(loaded.scene, {
      label: doc.target.label,
      ...(doc.target.selectSkeleton !== undefined ? { selectSkeleton: doc.target.selectSkeleton } : {}),
    });
    const selectedMeshes = new Set(selectedList);
    // The seat source for a subset skin that never bound the owner is a skin
    // that BINDS the owner — never the largest (a garment with enough private
    // helpers can out-size the body while omitting the pelvis, Codex P2
    // #108). A skirt weighted only to Waist is legitimate under the
    // subset-skeleton layout, not a rig contradiction; the owner is seated by
    // bone OBJECT with the source's inverse bind before its paint is moved.
    const primary = seatSourceFor(selectedList, pelvisOwnership.owner) ?? undefined;
    for (const mesh of allSkinnedMeshes(loaded.scene)) {
      if (!selectedMeshes.has(mesh)) {
        bindMarkers.push({
          code: 'pelvis-reown-skin-skipped',
          detail: { mesh: mesh.name, reason: 'skinned to another skeleton — shares no bone with the selected rig' },
        });
        continue;
      }
      if (primary !== undefined && primary !== mesh) {
        const { seated } = seatSharedBone(mesh, primary, pelvisOwnership.owner);
        if (seated) {
          bindMarkers.push({ code: 'pelvis-owner-seated', detail: { mesh: mesh.name, owner: pelvisOwnership.owner, from: primary.name } });
        }
      }
      const report = reownPelvisFlesh(mesh, pelvisOwnership);
      pelvisReowns.push(report);
      if (report.skipped !== undefined) {
        // A skin of another skeleton in a multi-skeleton target (the selected
        // rig's pelvis is not in it, nor any donor) — not this rig's flesh.
        bindMarkers.push({ code: 'pelvis-reown-skin-skipped', detail: { mesh: mesh.name, reason: report.skipped } });
        continue;
      }
      bindMarkers.push({
        code: 'pelvis-flesh-reowned',
        detail: {
          owner: report.owner,
          station: pelvisOwnership.stationBone,
          vertices: report.verticesReowned,
          unseated: report.verticesUnseated,
          weightMoved: Number(report.weightMoved.toFixed(3)),
          evicted: report.influencesEvicted,
          largestEviction: Number(report.largestEviction.toFixed(4)),
          worstWeightSumError: report.worstWeightSumError,
        },
      });
    }
  }

  const hingeBinds: HingeReweightReport[] = [];
  if (hinges.length > 0) {
    // EVERY skinned mesh, never "the" mesh (Codex P2, #103): a clothed
    // subject carries body + garment meshes with arm-chain weights on both —
    // soleSkinnedMesh would refuse a subject that loaded fine before
    // correctives, and picking one would leave the other's crease weights
    // uncorrected. The seat is idempotent per skeleton (a shared skeleton
    // seats once); the band reweight runs per mesh geometry.
    const meshes = allSkinnedMeshes(loaded.scene);
    // ADR-028 §6: amplitude scales with GIRTH, measured at ingest from the
    // same bind the graft measured — the kernel graft (no mesh) scales by the
    // family's declared radius PRIOR × segment, which gives a slim and a
    // muscular arm of equal length the same millimetres of swell (Codex P2,
    // #107 round three). Each leaf's belly ring radius is read from the
    // pristine paint, BEFORE any weight moves, on every skinned mesh; the
    // SKIN is the innermost ring, so the smallest measured radius wins — a
    // garment sits outside the body, and a densely tessellated sleeve must
    // never out-vote it by vertex count (Codex P2, #107 round four). A leaf
    // whose ring holds too little to measure anywhere keeps the prior — the
    // same radius units, so the ramp's calibration holds — and SAYS so.
    const girthOf = new Map<string, LeafGirthReading>();
    for (const hinge of hinges) {
      for (const leaf of hinge.leaves) {
        let innermost: LeafGirthReading | null = null;
        let measurable = 0;
        for (const mesh of meshes) {
          const reading = measureLeafGirth(mesh, hinge, leaf);
          if (reading === null) continue;
          measurable += 1;
          if (innermost === null || reading.radiusWorld < innermost.radiusWorld) innermost = reading;
        }
        if (innermost === null) {
          bindMarkers.push({
            code: 'hinge-leaf-girth-unmeasured',
            detail: {
              leaf: leaf.name,
              scaleWorld: Number(leaf.scaleWorld.toFixed(4)),
              reason: 'no skinned mesh holds enough ring flesh — the declared radius prior stands',
            },
          });
          continue;
        }
        girthOf.set(leaf.name, innermost);
        bindMarkers.push({
          code: 'hinge-leaf-girth-measured',
          detail: {
            leaf: leaf.name,
            radiusWorld: Number(innermost.radiusWorld.toFixed(4)),
            priorWorld: Number(leaf.scaleWorld.toFixed(4)),
            segmentWorld: Number(leaf.segmentWorld.toFixed(4)),
            vertices: innermost.vertices,
            meshesMeasured: measurable,
            meshes: meshes.length,
          },
        });
      }
    }
    hinges = hinges.map((h) => ({
      ...h,
      leaves: h.leaves.map((leaf) => {
        const g = girthOf.get(leaf.name);
        return g === undefined ? leaf : { ...leaf, scaleWorld: g.radiusWorld };
      }),
    }));
    const perHelper = new Map<string, HingeReweightReport>();
    const accumulate = (report: HingeReweightReport): void => {
      const prior = perHelper.get(report.helper);
      perHelper.set(
        report.helper,
        prior === undefined
          ? report
          : {
              helper: report.helper,
              verticesReweighted: prior.verticesReweighted + report.verticesReweighted,
              verticesUnseated: prior.verticesUnseated + report.verticesUnseated,
              influencesEvicted: prior.influencesEvicted + report.influencesEvicted,
              largestEviction: Math.max(prior.largestEviction, report.largestEviction),
              worstWeightSumError: Math.max(prior.worstWeightSumError, report.worstWeightSumError),
            },
      );
    };
    for (const mesh of meshes) {
      bindGraftedDigits(
        mesh,
        loaded.scene,
        rig,
        hinges.flatMap((h) => [h.helper, ...h.leaves.map((l) => l.name)]),
        [],
      );
      for (const hinge of hinges) {
        if (!adoptedCorrectives.has(hinge.helper)) accumulate(reweightHingeBand(mesh, rig, hinge));
        // E2: each leaf's belly takes its radial share AFTER the band, so the
        // band's transfer participates in the chain weight the belly reads.
        for (const leaf of hinge.leaves) {
          if (!adoptedCorrectives.has(leaf.name)) accumulate(reweightMuscleBelly(mesh, hinge, leaf));
        }
      }
    }
    // ---- ADR-028 E3: derive and graft the crease morphs (D2, D5) ----
    // LAST in the hinge pass, and that ordering is the design: E3 corrects
    // what is LEFT after E1's band and E2's bellies have moved weight, so the
    // derivation must run on the finished paint. Dormant until a family
    // template declares morphs — a hinge with none costs nothing here.
    for (const mesh of meshes) {
      const declared = hinges.flatMap((h) => h.profile.morphs.map((m) => ({ hinge: h, morph: m })));
      if (declared.length === 0) break;
      const position = mesh.geometry.getAttribute('position');
      if (position === undefined) continue;
      // The rest detail is a property of the BIND mesh, so it is captured ONCE
      // per mesh and reused by every stage of every hinge — the smoothing is
      // the expensive half of the derivation.
      //
      // LAZY, and that matters on the everyday path: a re-imported augmented
      // character adopts every crease it already wears, so an eager capture
      // would run 60 smoothing passes over every mesh to derive nothing at all
      // (Sol, review of #113). Deferred, a reopen pays nothing.
      let restDetail: ReturnType<typeof captureRestDetail> | null = null;
      const restDetailOf = (): ReturnType<typeof captureRestDetail> => {
        if (restDetail === null) {
          // THROUGH THE ATTRIBUTE API, never `.array` (Codex P2, #113): an
          // interleaved accessor's `array` is the whole backing buffer —
          // every other attribute included — so the graph would be built on a
          // wrong vertex count and the derivation would refuse the document.
          // getX/Y/Z also un-normalize a quantized accessor, which `.array`
          // hands over raw.
          // ...and through morphedBindPosition, so an active body-shape morph
          // is part of the body the graph describes. The rest capture and the
          // posed skinning must agree on which body that is, or the mush
          // reference and the LBS baseline are two different characters.
          const packed = new Float32Array(position.count * 3);
          for (let v = 0; v < position.count; v += 1) {
            const p = morphedBindPosition(mesh, v);
            packed[v * 3] = p.x;
            packed[v * 3 + 1] = p.y;
            packed[v * 3 + 2] = p.z;
          }
          restDetail = captureRestDetail(packed, mesh.geometry.getIndex()?.array ?? null);
        }
        return restDetail;
      };
      for (const { hinge, morph } of declared) {
        // BOTH names, and the dictionary is the load-bearing one: three writes
        // a morph's name onto the BufferAttribute when WE build it, but a
        // re-imported GLB restores names only into the mesh's
        // morphTargetDictionary (from `extras.targetNames`) — the attributes
        // come back anonymous. Reading the attribute alone never recognises a
        // re-imported crease, so every reopen paid for a full derivation it
        // then discarded, and said "grafted" about a target it had adopted.
        const already =
          (mesh.geometry.morphAttributes['position'] ?? []).some((a) => a.name === morph.name) ||
          mesh.morphTargetDictionary?.[morph.name] !== undefined;
        if (already) {
          // A re-imported augmented export already wears this crease. Adopt it
          // and skip the derivation entirely — re-deriving would be expensive
          // AND wrong, since the mesh it would measure already carries the
          // correction (#110's law, the same one the helpers obey).
          bindMarkers.push({ code: 'crease-morph-adopted', detail: { morph: morph.name, hinge: hinge.helper } });
          continue;
        }
        const driverBone = rig.bones.find((b) => b.name === hinge.driver);
        const endBone = rig.bones.find((b) => b.name === hinge.end);
        if (driverBone === undefined || endBone === undefined) continue;
        const thetaDeg = derivationAngle(hinge, morph.name);
        const restore = poseHingeAt(loaded.scene, rig, hinge, thetaDeg);
        let derivation;
        try {
          derivation = deriveCreaseDeltas(mesh, restDetailOf(), {
            jointWorldBind: driverBone.worldBind.position,
            endWorldBind: endBone.worldBind.position,
            // E1's own chain rule (reweight-hinge-band's isChain), so the
            // crease is derived for exactly the flesh the band reweights.
            // EXPERIMENT 2026-09-08: the helper and the leaves are THIS limb's
            // flesh too. The reweight has already moved band weight onto the
            // helper by the time this runs, so a chain of driver+parent+twists
            // drops the helper-weighted rim — measured on amari, 89 of the 221
            // vertices in the crease region — and derives them ZERO correction
            // beside neighbours carrying full correction. A hard edge inside
            // the crease, made by the region rule. Spike source under test.
            chainBones: rig.bones
              .map((b) => b.name)
              .filter(
                (n) =>
                  n === hinge.driver ||
                  n === hinge.parent ||
                  n === hinge.helper ||
                  hinge.leaves.some((l) => l.name === n) ||
                  n.startsWith(`${hinge.driver}Twist`) ||
                  n.startsWith(`${hinge.parent}Twist`),
              ),
            bandWorld: hinge.bandWorld,
            // A tenth of a percent of the band: below this a vertex is not
            // part of the crease, it is mush disagreeing with LBS the little
            // it disagrees everywhere.
            minDelta: hinge.bandWorld * 1e-3,
            // ADR-036's field read: "both of the elbow blend techniques ...
            // are needed to complete the softening effect on the blend."
            // Feather the correction past the band, then press down what that
            // raises. Both always, never a selector — Allen's verdict was that
            // the PAIR is right, and the study's own doc says the same.
            soften: {
              hingeAxisWorld: quatRotateVec3(driverBone.worldBind.rotation, hinge.profile.hingeAxisLocal),
            },
          });
        } finally {
          restore();
        }
        if (derivation.soften !== null) {
          // The softening moved flesh the band never claimed, so it says how
          // much. `bindMarkers` had no reader in this app for its whole life
          // until ADR-036's status line; a count nobody can reach is a count
          // that was never taken (the export door's "what it left out" rule).
          bindMarkers.push({
            code: 'crease-morph-softened',
            detail: {
              morph: morph.name,
              hinge: hinge.helper,
              thetaDeg,
              seeded: derivation.soften.seeded,
              allowed: derivation.soften.allowed,
              extended: derivation.soften.extended,
              despiked: derivation.soften.despiked,
            },
          });
        }
        if (derivation.deltas.length === 0) {
          // Loudly, not silently: a declared crease that derived NOTHING would
          // otherwise refuse at mount with no clue why, and the reason lives
          // here — no vertex in the band moved enough to matter.
          bindMarkers.push({
            code: 'crease-morph-empty',
            detail: { morph: morph.name, hinge: hinge.helper, thetaDeg, inBand: derivation.verticesInBand },
          });
          continue;
        }
        const report = graftCreaseMorph(mesh, morph.name, derivation.deltas);
        bindMarkers.push({
          code: 'crease-morph-grafted',
          detail: {
            morph: morph.name,
            hinge: hinge.helper,
            thetaDeg,
            vertices: report.verticesMoved,
            inBand: derivation.verticesInBand,
            refused: derivation.verticesRefused,
            amplified: derivation.verticesAmplified,
            framesLost: derivation.framesLost,
            largestDelta: Number(derivation.largestDelta.toFixed(5)),
          },
        });
      }
    }
    for (const report of perHelper.values()) {
      hingeBinds.push(report);
      bindMarkers.push({
        code: 'hinge-band-reweighted',
        detail: {
          helper: report.helper,
          vertices: report.verticesReweighted,
          unseated: report.verticesUnseated,
          evicted: report.influencesEvicted,
          worstSumError: report.worstWeightSumError,
          meshes: meshes.length,
        },
      });
    }
  }

  // ---- ADR-038 J2: the soft-tissue leaves. LAST of the weight passes on
  //      purpose: the chest repair (ADR-021), the pelvis re-own (ADR-029) and
  //      the hinge bands (ADR-028) have all written their paint, so the regions
  //      are MEASURED on the finished paint (the buttock the pelvis now owns is
  //      the glute; a coat tail the thighs own is not), the leaves join the rig
  //      and grow their nodes here, and each leaf takes only its share of each
  //      region vertex — every influence already there keeps its proportion of
  //      the rest, and no vertex outside a region is touched. At rest the
  //      leaf's world·IBM is the identity, so the bind mesh does not move.
  //      Nothing earlier reads a leaf: they are outside every profile, so the
  //      clips retargeted above are the same with or without them.
  let softTissuePlan: SoftTissuePlan;
  try {
    softTissuePlan = planSoftTissue(doc, loaded.scene, rig);
  } catch (err) {
    if (err instanceof SoftTissueRunRefusal) throw new DocumentRefusal(err.message);
    throw err;
  }
  rig = softTissuePlan.rig;
  bindMarkers.push(...softTissuePlan.markers);
  if (softTissuePlan.pending.length > 0) {
    materializedBones.push(...materializeMissingBones(loaded.scene, rig.bones));
    const mesh = soleSkinnedMesh(loaded.scene);
    for (const pending of softTissuePlan.pending) {
      const report = bindGraftedDigits(mesh, loaded.scene, rig, pending.boneNames, pending.influences, undefined, pending.donor ?? undefined);
      handBinds.push({
        handBone: `softTissue:${pending.region}`,
        bonesAdded: report.bonesAdded.length,
        verticesReweighted: report.verticesReweighted,
        influencesEvicted: report.influencesEvicted,
        largestEviction: report.largestEviction,
        worstWeightSumError: report.worstWeightSumError,
      });
    }
  }
  // ---- ADR-039 T2a: a confirmed ATTACHED hair softens — part of the soft-tissue
  //      pass, after the J2 leaves' seat: Harry's own weights feathered into the
  //      region's border (written to the region's vertices only), then the hair's
  //      leaf (`HairJiggle_graft`, a child of the Head) through the ONE seat with
  //      the Head as donor, so at rest the hair follows the head exactly as before.
  //      A free/mixed hair, a stale zone, a file that already wears the leaf: no
  //      change, and the record says why.
  const hairPlan = planHairSoftening(doc, loaded.scene, rig, zoneFingerprint);
  rig = hairPlan.rig;
  bindMarkers.push(...hairPlan.markers);
  if (hairPlan.pending !== null) {
    materializedBones.push(...materializeMissingBones(loaded.scene, rig.bones));
    const report = bindGraftedDigits(soleSkinnedMesh(loaded.scene), loaded.scene, rig, hairPlan.pending.boneNames, hairPlan.pending.influences, undefined, hairPlan.pending.donor);
    handBinds.push({
      handBone: 'softTissue:Hair',
      bonesAdded: report.bonesAdded.length,
      verticesReweighted: report.verticesReweighted,
      influencesEvicted: report.influencesEvicted,
      largestEviction: report.largestEviction,
      worstWeightSumError: report.worstWeightSumError,
    });
  }
  const softTissueGrafts: readonly SoftTissueGraftRecord[] = hairPlan.graft === null ? softTissuePlan.records : [...softTissuePlan.records, hairPlan.graft];

  // ---- ADR-038 §14 (J4, a prototype behind `RunOptions.surfaceJiggle`): every bounce
  //      leaf's membrane modes — grown this run, or declared by the file (a reopened
  //      export) — grafted as morph targets NOW: after the seat, before the mesh first
  //      draws (a renderer does not re-read targets added later).
  let surfaceJiggle: SurfaceJiggleRecord[] = [];
  if (options.surfaceJiggle === true) {
    // The hair's modes solve on its confirmed region with its held set (the scalp, the body, the face);
    // a file that already wears its hair solves on the patch its own surface.Hair targets were solved on.
    const leaves = surfaceJiggleLeaves(loaded.scene, softTissueGrafts).map((l) => {
      if (l.region !== 'Hair') return l;
      const patch = hairPlan.patch ?? adoptedHairPatch(soleSkinnedMesh(loaded.scene));
      return patch !== null ? { ...l, patch } : l;
    });
    if (leaves.length > 0) surfaceJiggle = graftSurfaceJiggle(soleSkinnedMesh(loaded.scene), subjectArrays(loaded.scene), leaves);
    for (const r of surfaceJiggle) {
      bindMarkers.push({
        code: r.refusal === null ? 'surface-jiggle-grafted' : 'surface-jiggle-refused',
        detail: { region: r.region, modes: r.modes.length, vertices: r.vertices, solveMs: Math.round(r.solveMs), ...(r.refusal === null ? {} : { refusal: r.refusal }) },
      });
    }
  }

  // ---- ADR-030: head seating rides the armature. STRICTLY after the clips
  //      are retargeted (§12's cure: the clips are made against the
  //      un-reseated bind, so they stay generic and the animation-only door
  //      writes the same bytes whatever the seating) and after every bind
  //      measurement above — nothing downstream of retarget reads Neck/Head,
  //      which is what makes this the run's LAST surgery, and what makes the
  //      app's in-place commit (main.ts) equal to a re-run: the seating probe
  //      pins that equality. The Neck's bind rotates about the character's
  //      side axis through its own origin, its subtree rigid, every inverse
  //      bind below it recomputed; the flesh does not move at rest and wears
  //      the seating at play — on our clips, and on any clip World layers by
  //      name onto the exported armature.
  //
  //      ABSENT `edits.headSpace` = the measured per-pair baseline (the same
  //      default the display era applied per clip, now ONE value for the ONE
  //      armature: measured once per selected source, sources that disagree
  //      are named and the first selection wins). PRESENT, zero included, =
  //      the explicit declaration. A rig that cannot carry the property
  //      refuses an explicit non-zero LOUDLY (§7) and records why otherwise —
  //      "off" stays distinguishable from "never ran" (Loud-Mothball).
  // ---- ADR-030 §13 (ruled 2026-08-28): arm-space is a play-time abduction the
  //      file DECLARES on its upper-arm nodes. Read here so the app's filter can
  //      seed from it (absent `edits.armSpace` = the file's declaration) and a
  //      reopened export wears what was set. A rig that cannot carry arm-space
  //      has nothing to read; a malformed or inconsistent declaration refuses the
  //      run — reading it as "none" would silently drop a set value.
  const armSpaceFile = ((): number | null => {
    let bones: readonly string[];
    try {
      bones = armSpacePlan(rig, 1).map((r) => r.bone);
    } catch (err) {
      if (err instanceof ArmSpaceRefusal) return null;
      throw err;
    }
    try {
      // Each bone's declaration must be what the plan derives for the file's character value
      // (Codex P2, #131): a reader applying per-bone values and this app previewing the plan
      // must be acting on one fact.
      const facing = armSpacePlan(rig, 1)[0]!.axis;
      const planFor = (character: number) =>
        character === 0
          ? bones.map((bone) => ({ bone, degrees: 0, axis: facing }))
          : armSpacePlan(rig, character).map((r) => ({ bone: r.bone, degrees: (r.radians * 180) / Math.PI, axis: r.axis }));
      return readSceneArmSpace(
        loaded.scene,
        bones,
        {
          label: doc.target.label,
          ...(doc.target.selectSkeleton !== undefined ? { selectSkeleton: doc.target.selectSkeleton } : {}),
        },
        planFor,
      );
    } catch (err) {
      if (!(err instanceof ArmSpaceRefusal)) throw err;
      throw new DocumentRefusal(`"${doc.target.label}": ${err.reason} — refusing rather than dropping a declared arm-space.`);
    }
  })();

  let seatedRig = rig;
  const headSeating = ((): HeadSeatingRecord => {
    let refused: string | null = null;
    try {
      headSpaceReseat(rig, 0); // capability: resolves the chain, refuses naming why
    } catch (err) {
      if (!(err instanceof HeadSpaceRefusal)) throw err;
      refused = err.reason;
    }
    // The baseline, measured on the PRE-reseat bind, once per selected source.
    const measured: { source: string; degrees: number }[] = [];
    if (refused === null) {
      const seen = new Set<string>();
      for (const clipRun of clips) {
        if (seen.has(clipRun.sourceLabel)) continue;
        seen.add(clipRun.sourceLabel);
        try {
          measured.push({
            source: clipRun.sourceLabel,
            degrees: (doc.target.family === 'meshy' ||
              (doc.target.family === 'unknown' && detectRigFamily(rig) === 'meshy')) &&
              detectRigFamily(sources.get(clipRun.sourceLabel)!.rig) === 'mixamo'
              ? meshyHeadBaseline(deps.ontology, priors, sources.get(clipRun.sourceLabel)!.rig, rig, clipRun.selection.profile)
              : headSeatingDeltaDegrees(sources.get(clipRun.sourceLabel)!.rig, rig),
          });
        } catch (err) {
          if (!(err instanceof HeadSpaceRefusal)) throw err;
          bindMarkers.push({ code: 'head-seating-baseline-unmeasurable', detail: { source: clipRun.sourceLabel, reason: err.reason } });
        }
      }
    }
    const baseline: number | null = measured[0]?.degrees ?? null;
    const source = measured[0]?.source ?? null;
    if (measured.length > 1) {
      const spread = Math.max(...measured.map((m) => m.degrees)) - Math.min(...measured.map((m) => m.degrees));
      if (spread > 0.5) {
        bindMarkers.push({
          code: 'head-seating-baseline-spread',
          detail: {
            chosen: measured[0]!.source,
            spreadDeg: Number(spread.toFixed(2)),
            sources: measured.map((m) => `${m.source}=${m.degrees.toFixed(1)}`).join(', '),
          },
        });
      }
    }
    const explicit = doc.edits.headSpace;
    if (refused !== null) {
      if (explicit !== undefined && explicit !== 0) {
        throw new DocumentRefusal(
          `head-space ${explicit}° is declared, but "${doc.target.label}" cannot carry it — ${refused} ` +
            `(ADR-030 §7: a seating the armature cannot wear fails the run, not a display filter).`,
        );
      }
      const provenance: HeadSeatingRecord['provenance'] =
        explicit !== undefined ? 'explicit' : baseline !== null ? 'baseline' : 'unmeasured';
      bindMarkers.push({ code: 'head-seating-skipped', detail: { reason: refused, provenance } });
      return { degrees: 0, total: 0, provenance, baseline, file: null, source, bone: null, refused };
    }
    const seat = rig.mapping.find((m) => m.ontologyId === HEAD_SPACE_SEAT)!.rigBone; // unique: the capability probe passed
    const selection = {
      label: doc.target.label,
      ...(doc.target.selectSkeleton !== undefined ? { selectSkeleton: doc.target.selectSkeleton } : {}),
    };
    // What the file DECLARES (ADR-030 §14), read before deciding. A malformed
    // marker fails the run: reading it as "nothing declared" would silently
    // re-run the heuristic on a neck the file may already say a person set.
    let file: HeadSeatingMarker | null;
    try {
      file = readSceneHeadSeatingMarker(loaded.scene, seat, selection);
    } catch (err) {
      if (!(err instanceof HeadSpaceRefusal)) throw err;
      throw new DocumentRefusal(
        `"${doc.target.label}": ${err.reason} — the file is inconsistent; refusing rather than re-seating a neck it may already declare.`,
      );
    }
    const decision = decideHeadSeating({ baseline, explicit, file, source });
    if (decision.clampedFrom !== null) {
      // A loud clamp, never a silent one: the band is the schema's law, and a
      // seating beyond it is a specimen worth hearing about.
      bindMarkers.push({
        code: 'head-seating-baseline-clamped',
        detail: { total: Number(decision.clampedFrom.toFixed(2)), band: HEAD_SPACE_MAX_DEGREES },
      });
    }
    const record: HeadSeatingRecord = {
      degrees: decision.degrees,
      total: decision.total,
      provenance: decision.provenance,
      baseline,
      file,
      source,
      bone: seat,
      refused: null,
    };
    // The surgery is the DIFFERENCE from what the file declares — ungated,
    // because two in-band totals may differ by more than the band.
    const reseat = headSpaceReseatDelta(rig, decision.degrees);
    if (reseat === null) {
      bindMarkers.push({
        code: 'bone-reseated',
        detail: {
          bone: seat,
          degrees: 0,
          total: Number(decision.total.toFixed(4)),
          declared: file === null ? 'none' : `${file.degrees.toFixed(4)}:${file.provenance}`,
          provenance: decision.provenance,
          axis: 'side',
        },
      });
    } else {
      const report = reseatSceneBoneBind(loaded.scene, reseat.bone, reseat, selection);
      seatedRig = reseatBoneBind(rig, reseat.bone, reseat);
      bindMarkers.push({
        code: 'bone-reseated',
        detail: {
          bone: reseat.bone,
          degrees: Number(decision.degrees.toFixed(4)),
          total: Number(decision.total.toFixed(4)),
          declared: file === null ? 'none' : `${file.degrees.toFixed(4)}:${file.provenance}`,
          provenance: decision.provenance,
          axis: 'side',
          descendants: report.descendants.length,
          skeletons: report.skeletons,
        },
      });
    }
    // The file's declaration, kept current: the exported armature says what
    // it wears and who decided, so its reopen agrees with this run.
    writeSceneHeadSeatingMarker(loaded.scene, seat, decision.marker, selection);
    return record;
  })();

  // ---- Authored clips (ADR-006 §13.4): the keys the `key.*` verbs committed
  //      become playable ClipData — the projection the mixer samples (§5.2
  //      rule 4: undo a key and there is no second store to re-assert it).
  //      A track naming a bone the rig does not have refuses like a pose edit
  //      (§8) — validated post-graft, so keys on grafted digits are legal.
  //      Keyless clips are REPORTED, never silently skipped: a caller decides
  //      what an unplayable clip means for its surface.
  const rigBoneNames = new Set(rig.bones.map((b) => b.name));
  const emptyAuthoredClips: string[] = [];
  const authoredClips: { name: string; clip: ClipData }[] = [];
  for (const authored of doc.clips) {
    for (const track of authored.tracks) {
      if (!rigBoneNames.has(track.bone)) {
        throw new DocumentRefusal(
          `authored clip "${authored.name}": track targets "${track.bone}" — no such bone in ` +
            `"${doc.target.label}" (§8: a Document naming a bone that does not exist fails at load, loudly).`,
        );
      }
      // The store gate refuses NEW commands on corrective helpers; a LOADED
      // Document is the other door to the same law (Codex P2, #103): a key on
      // a `_corr` bone would collide with the bake door as an apparent
      // double-bake, and the hinge driver owns that bone every frame anyway.
      if (isCorrectiveBone(track.bone)) {
        throw new DocumentRefusal(
          `authored clip "${authored.name}": track targets corrective helper "${track.bone}" — ` +
            `helpers are DERIVED from their driver's bend and cannot carry authored keys (ADR-028 §6e).`,
        );
      }
    }
    if (hasPlayableKeys(authored)) {
      authoredClips.push({ name: authored.name, clip: authoredClipData(authored, doc.fps.value) });
    } else {
      emptyAuthoredClips.push(authored.name);
    }
  }

  // ---- The pose layer (ADR-007 §5, replacing the #50 refusal now that the
  //      semantics are designed): per-bone local-rotation overrides projected
  //      onto the rest pose. Precedence is realized at play time — a playing
  //      clip's mixer re-samples the bones its tracks drive every tick, so
  //      the pose layer holds exactly the bones the active clip does NOT
  //      drive (§5.2), and undoing a pose cannot be re-asserted by anything:
  //      there is no second substrate (§5.4). Applied AFTER materialization
  //      so grafted digits are addressable; a name miss refuses loudly (§8).
  const posedBones: string[] = [];
  const poseRests: { bone: string; rotation: { x: number; y: number; z: number; w: number } }[] = [];
  for (const edit of doc.edits.poses) {
    const node = loaded.scene.getObjectByName(edit.bone);
    if (!node) {
      throw new DocumentRefusal(
        `pose edit targets "${edit.bone}" — no such node in "${doc.target.label}" ` +
          `(§8: a Document naming a bone that does not exist fails at load, loudly, naming it).`,
      );
    }
    // Same law as authored tracks (Codex P2, #103): the graft made the helper
    // a real node, but a loaded pose on it would be silently overwritten by
    // the per-frame hinge driver — the exact contradiction the store gate
    // refuses for live commands. A law needs every door.
    if (isCorrectiveBone(edit.bone)) {
      throw new DocumentRefusal(
        `pose edit targets corrective helper "${edit.bone}" — helpers are DERIVED from their ` +
          `driver's bend; the hinge driver would overwrite this pose every frame (ADR-028 §6e).`,
      );
    }
    // Capture the imported rest BEFORE overwriting, so a live projector can
    // restore it when this pose is later cleared/undone (Codex P2, #54).
    const { x, y, z, w } = node.quaternion;
    poseRests.push({ bone: edit.bone, rotation: { x, y, z, w } });
    node.quaternion.set(edit.rotation.x, edit.rotation.y, edit.rotation.z, edit.rotation.w);
    posedBones.push(edit.bone);
  }

  return {
    targetImport,
    rig,
    seatedRig,
    headSeating,
    armSpaceFile,
    scene: loaded.scene,
    materializedBones,
    handGrafts,
    handBinds,
    tailGrafts,
    zoneFingerprint,
    faceForward: faceForward === null ? null : { turned: faceForward.turned, reason: faceForward.reason },
    softTissueGrafts,
    hair: finishHairRecord(hairPlan.record, surfaceJiggle.find((r) => r.region === 'Hair') ?? null, options.surfaceJiggle === true),
    surfaceJiggle,
    hinges,
    hingeBinds,
    pelvisReowns,
    confirmedElbows: [...confirmedElbows],
    bindMarkers,
    jointFrames,
    posedBones,
    poseRests,
    authoredClips,
    emptyAuthoredClips,
    sources,
    clips,
    slDroppedJoints: [...sources].flatMap(([source, s]) =>
      'droppedJoints' in s && s.droppedJoints.length > 0 ? [{ source, joints: s.droppedJoints }] : [],
    ),
  };
}
